import {operationalConnection,operationalLabel,operationalFailureMessage,retainOperationalSnapshot,operationalStatusLabel} from './operational-view';
import {mergeOperationalOrderPage} from './order-pages';
import {smmMenuCards,smmInitialCategory,type SmmMenuCard} from './menu-cards';
import type {BentoFamilyViewV1} from '../../../contracts/bento-family-view-v1';
import type {MobileOrderQuery} from '../../../contracts/mobile-order-pages-v1';
import {SyncDiagnostics} from './SyncDiagnostics';
import {FocusDialog,PinKeypad} from './UiKit';
import {PendingDrinkSheet} from './PendingDrinkSheet';
import {BusinessDayReceipts} from './BusinessDayReceipts';
import {PaymentReviewSheet} from './PaymentReviewSheet';
import {CancelOrderSheet} from './CancelOrderSheet';
import {syncFailureMessage} from './sync-diagnostics';
import {priceSmmRiceballCart} from './riceball-promotion';
import {useEffect,useMemo,useRef,useState} from 'react';
import {readSmmLocalWorkspace,writeSmmLocalWorkspace,createSmmPendingIntent,type SmmLocalPreferences} from './persistence';
import {resolveSmmRuntimePort} from './runtime';
import {pairSmmLan,probeSmmLan,readSmmLanPwaConfig,saveSmmLanPwaConfig} from './pwa-lan';
import {forgetSmmStaffSession,clearSmmStaffSession,listSmmStaff,readSmmStaffSession,refreshSmmStaffSession,verifySmmStaff,type SmmStaffDirectoryItem,type SmmStaffSession} from './pwa-staff';
import {
  comboSelectionStateFromIntent,
  comboChildSelectionStateFromIntent,
  findSmmComboChoice,
  publishedSmmComboUnitMinor,
  revalidateSmmCartComboIntent,
  resolveSmmProductCombo,
  selectedSmmCartOptions,
  selectedSmmComboIntent,
  smmComboCards,
  changeSmmComboChoice,
  smmCartDisplayDetails,frontlineOptionName,
  toggleSmmSelection,
  initialSmmSelections,
  validateSmmComboSelections,
  validateSmmSelections,
  type SmmComboSelectionState,
  type SmmComboChildSelectionState,
  type SmmResolvedComboChoice,
  type SmmSelectionState,
} from './selection';
import {
  acceptSmmCartRefresh,
  buildSmmCartRefreshAttention,
  sameSmmCartRefreshAttention,
  smmLineTotalMinor,
} from './stage3-cart.mjs';
import {
  SMM_STAGE4_TENDERS,
  smmStage4CheckoutReady,
  smmStage4DiningTargetStatus,
  smmStage4TenderLabel,
} from './stage4-checkout.mjs';
import {Stage5SubmitView,type SmmStage5Session} from './Stage5Submit';
import {Stage6QueueView} from './Stage6Queue';
import {Stage7OrdersView} from './Stage7Orders';
import {Stage8DineView} from './Stage8Dine';
import {Stage9MoreView,type Stage9Tool} from './Stage9More';
import {StageXState} from './StageXState';
import {smmCartAfterIntent,smmIntentOwnsCart,smmStage5ConfirmedDisplayCode,smmStage5RepairPath,smmStage5SubmissionShortRef} from './stage5-submit.mjs';
import {createSingleFlightRefresh} from './refresh-single-flight.mjs';
import './stage1.css';
import './stage2.css';
import './stage3.css';
import './stage4.css';
import './stage5.css';
import './stage6.css';
import './stage7.css';
import './stage8.css';
import './stage9.css';
import './stagex.css';
import type {
  SmmCartLine,
  SmmConnectionState,
  SmmCommandResult,
  SmmOrderProjection,
  SmmPendingIntent,
  SmmProduct,
  SmmQuoteSnapshot,
  SmmReadModelSnapshot,
  SmmRuntimePort,
  SmmServiceMode,
  SmmTender,
  SmmDiningTarget,
  SmmCommandReadback,
  SmmStoreCommandInput,
} from './product-types';

type View='order'|'work'|'orders'|'dine'|'more';

const nowIso=()=>new Date().toISOString();
const money=(currency:string,minor:number)=>new Intl.NumberFormat('zh-HK',{style:'currency',currency,currencyDisplay:currency==='HKD'?'narrowSymbol':'symbol',minimumFractionDigits:minor%100===0?0:2,maximumFractionDigits:2}).format(minor/100).replace(/^-/,'−');

function publishedAdjustmentLabel(minor?:number){
  return typeof minor==='number'&&Number.isSafeInteger(minor)&&minor!==0?' '+(minor>0?'+':'')+money('HKD',minor):'';
}

function productMediaSrc(product:Pick<SmmProduct,'name'|'productId'|'imageRef'>){
  const exact=String(product.imageRef??'').trim();
  return exact||null;
}

function ProductMedia({product,className}:{product:Pick<SmmProduct,'name'|'productId'|'imageRef'>;className:string}){
  const source=productMediaSrc(product);
  return source?<span className={className}><img src={source} alt={product.name} loading="lazy"/></span>:null;
}

export function App(){
  const initial=useMemo(()=>readSmmLocalWorkspace(),[]);
  const [view,setView]=useState<View>(initial.preferences.activeView);
  const [activeCategoryId,setActiveCategoryId]=useState<string|null>(initial.preferences.activeCategoryId);
  const [sourceFilter]=useState(initial.preferences.sourceFilter);
  const [serviceMode,setServiceMode]=useState<SmmServiceMode>(initial.preferences.serviceMode);
  const [tender,setTender]=useState<SmmTender>(initial.preferences.tender);
  const [cart,setCart]=useState<readonly SmmCartLine[]>(initial.cart);
  const [pendingIntents,setPendingIntents]=useState<readonly SmmPendingIntent[]>(initial.pendingIntents);
  const [commandReadbacks,setCommandReadbacks]=useState(initial.commandReadbacks);
  const [staffSession,setStaffSession]=useState<SmmStaffSession|null>(()=>readSmmStaffSession());
  const staffIdentityRef=useRef(staffSession?.staffId??null);
  const [port]=useState<SmmRuntimePort|null>(()=>resolveSmmRuntimePort());
  const [connection,setConnection]=useState<SmmConnectionState>(port?'LOADING':'NOT_CONNECTED');
  const [browserOnline,setBrowserOnline]=useState(()=>navigator.onLine);
  const [snapshot,setSnapshot]=useState<SmmReadModelSnapshot|null>(null);
  const pageGeneration=useRef(0),pageLock=useRef(false);
  const [pagingBusy,setPagingBusy]=useState(false);
  const [pagingError,setPagingError]=useState<string|null>(null);
  const [notice,setNotice]=useState<string|null>(null);
  const [error,setError]=useState<string|null>(null);
  const [selectedProduct,setSelectedProduct]=useState<SmmProduct|null>(null);
  const [selections,setSelections]=useState<SmmSelectionState>({});
  const [selectedVariationId,setSelectedVariationId]=useState<string|null>(null);
  const [comboEnabled,setComboEnabled]=useState(false);
  const [comboSelections,setComboSelections]=useState<SmmComboSelectionState>({});
  const [comboChildren,setComboChildren]=useState<SmmComboChildSelectionState>({});
  const [cartOpen,setCartOpen]=useState(false);
  const [checkoutStage,setCheckoutStage]=useState(false);
  const [editingLineId,setEditingLineId]=useState<string|null>(null);
  const [cartNote,setCartNote]=useState(initial.cartNote);
  const workspaceRef=useRef({cart,cartNote,pendingIntents});
  workspaceRef.current={cart,cartNote,pendingIntents};
  const [submitting,setSubmitting]=useState(false);
  const submitLockRef=useRef(false);
  const [stage5Reading,setStage5Reading]=useState(false);
  const [stage5Session,setStage5Session]=useState<SmmStage5Session|null>(()=>{
    const restored=initial.pendingIntents.find(item=>(item.state==='PENDING'||item.state==='UNKNOWN'||item.state==='REJECTED')&&smmIntentOwnsCart(item,initial.cart));
    if(!restored)return null;
    return Object.freeze({
      intent:restored,
      state:restored.state==='REJECTED'?'REJECTED':'UNKNOWN',
      message:restored.state==='PENDING'?'上次提交被中斷；只會確認原本結果。':restored.lastMessage,
    });
  });
  const [search,setSearch]=useState('');
  const [searchOpen,setSearchOpen]=useState(false);
  const [moreTool,setMoreTool]=useState<Stage9Tool|null>(null);
  const [drinkOrderId,setDrinkOrderId]=useState<string|null>(null);
  const [orderAction,setOrderAction]=useState<{kind:'payment'|'cancel';orderId:string;displayCode?:string;onReadback:()=>void}|null>(null);
  const [dineTable,setDineTable]=useState('');
  const [dineCovers,setDineCovers]=useState(2);
  const [diningTarget,setDiningTarget]=useState<SmmDiningTarget|null>(initial.preferences.diningTarget);
  const [diningTargetOpen,setDiningTargetOpen]=useState(false);
  const [statusOpen,setStatusOpen]=useState(false);
  const [orderTab,setOrderTab]=useState<'PENDING'|'WORKING'|'READY'|'HISTORY'>('PENDING');

  const persist=(next:{
    cart?:readonly SmmCartLine[];
    pendingIntents?:readonly SmmPendingIntent[];
    cartNote?:string;
    preferences?:SmmLocalPreferences;
  })=>{
    const current=workspaceRef.current;
    workspaceRef.current={cart:next.cart??current.cart,cartNote:next.cartNote??current.cartNote,pendingIntents:next.pendingIntents??current.pendingIntents};
    writeSmmLocalWorkspace({
      ...workspaceRef.current,
      preferences:next.preferences??{activeView:view,activeCategoryId,sourceFilter,serviceMode,tender,diningTarget},
    });
  };

  const changeView=(next:View)=>{
    setView(next);
    persist({preferences:{activeView:next,activeCategoryId,sourceFilter,serviceMode,tender,diningTarget}});
  };

  const changeCategory=(next:string|null)=>{
    setActiveCategoryId(next);
    persist({preferences:{activeView:view,activeCategoryId:next,sourceFilter,serviceMode,tender,diningTarget}});
  };
  const runStoreCommand=async(input:SmmStoreCommandInput)=>{
    if(!port?.fulfillOrder)return Object.freeze({state:'NOT_CONNECTED' as const,message:'門店操作未連接'});
    try{return await port.fulfillOrder(input);}
    finally{setCommandReadbacks(readSmmLocalWorkspace().commandReadbacks);}
  };

  const refresh=useMemo(()=>createSingleFlightRefresh(async()=>{
    const generation=++pageGeneration.current;pageLock.current=false;setPagingBusy(false);setPagingError(null);
    if(!port){
      setConnection('NOT_CONNECTED');
      setSnapshot(null);
      return;
    }
    setConnection('LOADING');
    setError(null);
    try{
      const next=await port.readSnapshot();
      if(generation!==pageGeneration.current)return;
      setSnapshot(previous=>retainOperationalSnapshot(previous,next));
      setConnection('READY');
    }catch(reason){
      if(generation!==pageGeneration.current)return;
      console.warn('SMM_APP_REFRESH_DIAGNOSTIC',reason);
      setConnection('ERROR');
      setError(syncFailureMessage());
    }
  }),[port]);
  const changeStaffSession=(session:SmmStaffSession|null,reload=true)=>{
    const changed=staffIdentityRef.current!==(session?.staffId??null);
    if(changed){
      staffIdentityRef.current=session?.staffId??null;pageGeneration.current++;pageLock.current=false;setPagingBusy(false);setPagingError(null);
      setSnapshot(null);setConnection(session?'LOADING':'NOT_CONNECTED');
      setOrderAction(null);
    }
    setStaffSession(session);
    if(session&&reload){
      const generation=pageGeneration.current,reading=refresh();
      if(changed)void reading.then(()=>{if(staffIdentityRef.current===session.staffId&&pageGeneration.current===generation)void refresh();});
    }
  };
  const readOrderPage=async(query:MobileOrderQuery)=>{
    if(pageLock.current||connection!=='READY'||!snapshot?.orderPages||!port?.readOrderPage)return;
    const generation=pageGeneration.current;
    pageLock.current=true;setPagingBusy(true);setPagingError(null);
    try{
      const page=await port.readOrderPage(query);
      if(generation!==pageGeneration.current)return;
      const next=mergeOperationalOrderPage(snapshot,page,query);
      setSnapshot(previous=>previous===snapshot?next:previous);
    }catch(error){
      if(generation!==pageGeneration.current)return;
      const status=(error as {status?:number})?.status;
      setPagingError(status===409?'門店訂單已更新，請重新整理後再載入。原有資料已保留。':status===401?'登入需要重新確認。原有訂單及購物草稿已保留。':'暫時未能載入這頁，原有資料已保留；請重試或重新整理。');
    }finally{
      if(generation===pageGeneration.current){pageLock.current=false;setPagingBusy(false);}
    }
  };
  const initialRefreshRef=useRef(false);

  useEffect(()=>{
    if(!initialRefreshRef.current){
      initialRefreshRef.current=true;
      void refresh();
    }
    const unsubscribe=port?.subscribeConfigChanges?.(()=>void refresh())??(()=>{});
    const onOnline=()=>void refresh();
    const onPageShow=()=>void refresh();
    const onVisibility=()=>{if(document.visibilityState==='visible')void refresh();};
    window.addEventListener('online',onOnline);
    window.addEventListener('pageshow',onPageShow);
    document.addEventListener('visibilitychange',onVisibility);
    return()=>{
      unsubscribe();
      window.removeEventListener('online',onOnline);
      window.removeEventListener('pageshow',onPageShow);
      document.removeEventListener('visibilitychange',onVisibility);
    };
  },[refresh]);

  // Connectivity presentation only; retain the canonical snapshot, session and submission state.
  useEffect(()=>{
    const online=()=>setBrowserOnline(true);
    const offline=()=>setBrowserOnline(false);
    window.addEventListener('online',online);
    window.addEventListener('offline',offline);
    return()=>{window.removeEventListener('online',online);window.removeEventListener('offline',offline);};
  },[]);

  useEffect(()=>{
    let cancelled=false;
    void refreshSmmStaffSession().then(session=>{if(!cancelled)changeStaffSession(session,false);});
    return()=>{cancelled=true};
  },[]);

  const [,redrawSourceAge]=useState(0);
  useEffect(()=>{
    const source=snapshot?.operational;
    if(source?.freshness!=='CURRENT'||!source.observedAt)return;
    // Presentation-only expiry; never fetches business data or polls.
    const remaining=Date.parse(source.observedAt)+300001-Date.now();
    if(remaining<=0)return;
    const timer=window.setTimeout(()=>redrawSourceAge(value=>value+1),remaining);
    return()=>window.clearTimeout(timer);
  },[snapshot?.operational?.observedAt,snapshot?.operational?.freshness]);

  const menu=snapshot?.menu;
  const productPrice=(product:SmmProduct,mode:SmmServiceMode)=>{
    const value=mode==='DINE_IN'?product.publishedDineInUnitPriceMinor:product.publishedTakeawayUnitPriceMinor;
    return Number.isSafeInteger(Number(value))&&Number(value)>=0?Number(value):null;
  };

  const projectLineAgainstCurrentMenu=(line:SmmCartLine,mode:SmmServiceMode)=>{
    const product=menu?.products.find(item=>item.productId===line.productId);
    if(!menu||!product||!product.available){
      return Object.freeze({
        canAccept:false,
        configChanged:true,
        proposedSelections:line.selections,
        proposedCombo:undefined,
        proposedPublishedUnitPriceMinor:undefined,
      });
    }

    let configChanged=false;
    let selectionsValid=true;
    const refreshedSelections=line.selections.map(selection=>{
      const group=product.optionGroups.find(item=>item.optionGroupId===selection.optionGroupId);
      const option=group?.options.find(item=>item.optionId===selection.optionId&&item.available);
      if(!group||!option){
        selectionsValid=false;
        configChanged=true;
        return selection;
      }
      const adjustment=Number.isSafeInteger(Number(option.publishedAdjustmentMinor))
        ?Number(option.publishedAdjustmentMinor)
        :0;
      if(
        selection.optionName!==option.name||
        Number(selection.publishedAdjustmentMinor??0)!==adjustment
      )configChanged=true;
      return Object.freeze({
        optionGroupId:group.optionGroupId,
        optionId:option.optionId,
        optionName:option.name,
        publishedAdjustmentMinor:adjustment,
      });
    });

    if(line.selectedVariationId){
      const variation=product.variations?.find(item=>item.variationId===line.selectedVariationId&&item.available);
      if(!variation)selectionsValid=false;
    }else if(product.variationRequired){
      selectionsValid=false;
    }
    if(!selectionsValid)configChanged=true;

    const optionMinor=refreshedSelections.reduce(
      (sum,item)=>sum+(Number.isSafeInteger(Number(item.publishedAdjustmentMinor))?Number(item.publishedAdjustmentMinor):0),
      0,
    );
    const refreshedCombo=line.combo?revalidateSmmCartComboIntent(product,menu,line.combo,mode):null;
    if(line.combo&&!refreshedCombo){
      selectionsValid=false;
      configChanged=true;
    }
    if(refreshedCombo&&JSON.stringify(refreshedCombo)!==JSON.stringify(line.combo))configChanged=true;

    const base=line.combo?null:productPrice(product,mode);
    const comboUnit=refreshedCombo?publishedSmmComboUnitMinor(refreshedCombo,optionMinor,resolveSmmProductCombo({...product,comboId:refreshedCombo.comboId},menu,mode)?.mainAdjustmentMinor??0):null;
    const unitMinor=selectionsValid
      ?line.combo
        ?comboUnit??undefined
        :base!==null?base+optionMinor:undefined
      :undefined;

    return Object.freeze({
      canAccept:unitMinor!==undefined,
      configChanged,
      proposedSelections:Object.freeze(refreshedSelections),
      proposedCombo:refreshedCombo??undefined,
      proposedPublishedUnitPriceMinor:unitMinor,
    });
  };

  const repriceLine=(line:SmmCartLine,mode:SmmServiceMode):SmmCartLine=>{
    if(!menu)return line;
    const projected=projectLineAgainstCurrentMenu(line,mode);
    if(!projected.canAccept){
      const attention=buildSmmCartRefreshAttention({
        menuRevision:menu.revision,
        oldPublishedUnitPriceMinor:line.publishedUnitPriceMinor,
        proposedPublishedUnitPriceMinor:projected.proposedPublishedUnitPriceMinor,
        proposedSelections:projected.proposedSelections,
        proposedCombo:projected.proposedCombo,
        configChanged:true,
        canAccept:false,
        detectedAt:line.refreshAttention?.detectedAt??nowIso(),
      });
      return Object.freeze({...line,...(attention?{refreshAttention:attention}:{})});
    }
    const {refreshAttention:_staleAttention,...accepted}=line;
    return Object.freeze({
      ...accepted,
      selections:projected.proposedSelections,
      ...(line.combo&&projected.proposedCombo?{combo:projected.proposedCombo}:{}),
      publishedUnitPriceMinor:projected.proposedPublishedUnitPriceMinor,
    });
  };
  const publishedTotalMinor=cart.every(line=>Number.isSafeInteger(Number(line.publishedUnitPriceMinor))&&Number(line.publishedUnitPriceMinor)>=0)
    ?cart.reduce((sum,line)=>sum+Number(line.publishedUnitPriceMinor)*line.quantity,0)
    :null;
  const quote:SmmQuoteSnapshot|null=menu&&publishedTotalMinor!==null&&cart.length?Object.freeze({
    quoteId:'PUBLISHED-'+menu.revision,
    revision:menu.revision,
    currency:'HKD',
    totalMinor:publishedTotalMinor,
    lines:Object.freeze(cart.map(line=>Object.freeze({
      lineId:line.lineId,
      currency:'HKD',
      finalUnitPriceMinor:Number(line.publishedUnitPriceMinor),
      lineTotalMinor:Number(line.publishedUnitPriceMinor)*line.quantity,
    }))),
    observedAt:menu.observedAt,
  }):null;
  const menuPresentation=snapshot?.menuPresentation?.revision===menu?.revision?snapshot?.menuPresentation:undefined;
  const menuCards=smmMenuCards(menu,serviceMode,menuPresentation?.bentoFamilies,'',menuPresentation?.menuOrder);
  const categoryRank=new Map((menuPresentation?.menuOrder.categoryOrder??[]).map((id,index)=>[id,index]));
  const categories=(menu?.categories??[]).filter(category=>menuCards.some(card=>card.categoryId===category.categoryId))
    .slice().sort((a,b)=>(categoryRank.get(a.categoryId)??Infinity)-(categoryRank.get(b.categoryId)??Infinity));
  const effectiveCategoryId=smmInitialCategory(activeCategoryId,categories,menuCards);
  const visibleCards=search.trim()?smmMenuCards(menu,serviceMode,menuPresentation?.bentoFamilies,search,menuPresentation?.menuOrder)
    :menuCards.filter(card=>card.categoryId===effectiveCategoryId);
  useEffect(()=>{if(effectiveCategoryId&&effectiveCategoryId!==activeCategoryId)changeCategory(effectiveCategoryId);},[effectiveCategoryId,activeCategoryId]);
  const selectedFamily=menuCards.find(card=>card.family?.members.some(member=>member.productId===selectedProduct?.productId))?.family;
  const changeFamilyProduct=(productId:string)=>{
    const product=menu?.products.find(item=>item.productId===productId);
    if(!product?.available||!selectedFamily?.members.some(member=>member.productId===productId))return;
    setSelectedProduct(product);
    setSelections(initialSmmSelections(product.optionGroups));
    setSelectedVariationId(null);
    setComboEnabled(false);setComboSelections({});setComboChildren({});
  };


  useEffect(()=>{
    if(!menu||cart.length===0)return;
    let changed=false;
    let affected=0;
    const projectedCart=priceSmmRiceballCart(cart.map(line=>repriceLine(line,serviceMode)),menu,serviceMode);
    const next=cart.map(line=>{
      const baseProjection=projectLineAgainstCurrentMenu(line,serviceMode);
      const fullBasket=projectedCart.find(row=>row.lineId===line.lineId);
      const projected={...baseProjection,...(baseProjection.canAccept&&fullBasket?{proposedPublishedUnitPriceMinor:fullBasket.publishedUnitPriceMinor}:{})};
      const proposal=buildSmmCartRefreshAttention({
        menuRevision:menu.revision,
        oldPublishedUnitPriceMinor:line.publishedUnitPriceMinor,
        proposedPublishedUnitPriceMinor:projected.proposedPublishedUnitPriceMinor,
        proposedSelections:projected.proposedSelections,
        proposedCombo:projected.proposedCombo,
        configChanged:projected.configChanged,
        canAccept:projected.canAccept,
        detectedAt:line.refreshAttention?.detectedAt??nowIso(),
      });

      if(!proposal){
        if(!line.refreshAttention)return line;
        changed=true;
        const {refreshAttention:_staleAttention,...cleared}=line;
        return Object.freeze(cleared);
      }

      affected+=1;
      if(sameSmmCartRefreshAttention(line.refreshAttention,proposal))return line;
      changed=true;
      return Object.freeze({...line,refreshAttention:proposal});
    });

    if(!changed)return;
    const frozen=Object.freeze(next);
    setCart(frozen);
    writeSmmLocalWorkspace({
      cart:frozen,
      cartNote,
      pendingIntents,
      preferences:{activeView:view,activeCategoryId,sourceFilter,serviceMode,tender,diningTarget},
    });
    if(affected>0)setNotice('餐單有更新；請喺購物草稿逐項確認已標示商品。未確認前唔會靜默接受新價格或套餐資料。');
  },[menu?.revision,menu?.observedAt]);

  const resetProductEditor=()=>{
    setComboChildren({});
    setSelectedProduct(null);
    setSelections({});
    setSelectedVariationId(null);
    setComboEnabled(false);
    setComboSelections({});
    setEditingLineId(null);
  };

  const openCartLineEditor=(line:SmmCartLine)=>{
    const product=menu?.products.find(item=>item.productId===line.productId);
    if(!product){
      setNotice('呢件商品已不在目前餐單；請移除後重新選擇。');
      return;
    }
    const restored:Record<string,readonly string[]>={};
    for(const selection of line.selections){
      restored[selection.optionGroupId]=Object.freeze([
        ...(restored[selection.optionGroupId]??[]),
        selection.optionId,
      ]);
    }
    setEditingLineId(line.lineId);
    setSelectedProduct(line.combo?{...product,comboId:line.combo.comboId}:product);
    setSelections(Object.freeze(restored));
    setSelectedVariationId(line.selectedVariationId??null);
    setComboEnabled(Boolean(line.combo));
    setComboSelections(line.combo?comboSelectionStateFromIntent(line.combo):{});
    setComboChildren(line.combo?comboChildSelectionStateFromIntent(line.combo):{});
    setCartOpen(false);
  };

  const addSelectedProduct=(requestedQuantity?:number)=>{
    if(!selectedProduct)return;
    const validation=validateSmmSelections(selectedProduct,selections);
    if(!validation.ok){
      setNotice(validation.issues[0]??'請完成商品設定');
      return;
    }
    if(selectedProduct.variationRequired&&!selectedVariationId){
      setNotice('請先選擇必選規格');
      return;
    }
    const comboValidation=validateSmmComboSelections(selectedProduct,menu,comboEnabled,comboSelections,comboChildren);
    if(!comboValidation.ok){
      setNotice(comboValidation.issues[0]??'請完成套餐設定');
      return;
    }
    const variation=selectedProduct.variations?.find(item=>item.variationId===selectedVariationId);
    const selectedOptions=selectedSmmCartOptions(selectedProduct,selections);
    const optionMinor=selectedOptions.reduce((sum,item)=>sum+(Number.isSafeInteger(Number(item.publishedAdjustmentMinor))?Number(item.publishedAdjustmentMinor):0),0);
    const combo=selectedSmmComboIntent(selectedProduct,menu,comboEnabled,comboSelections,serviceMode,comboChildren);
    const baseMinor=productPrice(selectedProduct,serviceMode);
    const publishedUnitPriceMinor=combo
      ?publishedSmmComboUnitMinor(combo,optionMinor,resolveSmmProductCombo(selectedProduct,menu,serviceMode)?.mainAdjustmentMinor??0)
      :baseMinor===null?null:baseMinor+optionMinor;
    if(publishedUnitPriceMinor===null){
      setNotice('餐單／套餐價格資料未完整，請重新同步。');
      return;
    }
    const existingLine=editingLineId?cart.find(item=>item.lineId===editingLineId):undefined;
    const line:SmmCartLine=Object.freeze({
      lineId:existingLine?.lineId??crypto.randomUUID(),
      productId:selectedProduct.productId,
      productName:selectedProduct.name,
      quantity:Number.isSafeInteger(requestedQuantity)?Math.min(99,Math.max(1,requestedQuantity!)):existingLine?.quantity??1,
      ...(variation?{selectedVariationId:variation.variationId,selectedVariationName:variation.name}:{}),
      selections:selectedOptions,
      ...(combo?{combo}:{}),
      publishedUnitPriceMinor,
      createdAt:existingLine?.createdAt??nowIso(),
    });
    const rawNext=existingLine
      ?cart.map(item=>item.lineId===existingLine.lineId?line:item)
      :[...cart,line];
    const next=menu?priceSmmRiceballCart(rawNext,menu,serviceMode):rawNext;
    setCart(next);
    persist({cart:next});
    const edited=Boolean(existingLine);
    resetProductEditor();
    if(edited)setCartOpen(true);
    setNotice(edited
      ?'已更新商品。'
      :combo?'已加入套餐。':'已加入購物車。');
  };

  const updateCart=(raw:readonly SmmCartLine[])=>{
    const next=menu?priceSmmRiceballCart(raw,menu,serviceMode):raw;
    setCart(next);
    persist({cart:next});
  };

  const acceptCartRefresh=(lineId:string)=>{
    const result=acceptSmmCartRefresh(cart,lineId);
    if(!result.accepted){
      setNotice('呢一項需要重新編輯，未有可直接接受嘅完整更新。');
      return;
    }
    const next=menu?priceSmmRiceballCart(result.cart,menu,serviceMode):result.cart;
    setCart(next);
    persist({cart:next});
    setNotice('已更新商品。');
  };

  const changeServiceMode=(next:SmmServiceMode)=>{
    const repriced=cart.map(line=>repriceLine(line,next));
    const promoted=menu?priceSmmRiceballCart(repriced,menu,next):repriced;
    const nextDiningTarget=next==='TAKEAWAY'?null:diningTarget;
    setServiceMode(next);
    if(next==='TAKEAWAY')setDiningTarget(null);
    setCart(promoted);
    writeSmmLocalWorkspace({
      cart:promoted,
      cartNote,
      pendingIntents,
      preferences:{activeView:view,activeCategoryId,sourceFilter,serviceMode:next,tender,diningTarget:nextDiningTarget},
    });
  };

  const changeTender=(next:SmmTender)=>{
    setTender(next);
    persist({preferences:{activeView:view,activeCategoryId,sourceFilter,serviceMode,tender:next,diningTarget}});
  };

  const changeDiningTarget=(next:SmmDiningTarget|null)=>{
    const targetMode:SmmServiceMode=next?'DINE_IN':serviceMode;
    setDiningTarget(next);
    persist({preferences:{activeView:view,activeCategoryId,sourceFilter,serviceMode:targetMode,tender,diningTarget:next}});
  };

  const saveIntent=(intent:SmmPendingIntent)=>{
    const next=[intent,...workspaceRef.current.pendingIntents.filter(item=>item.submissionId!==intent.submissionId)].slice(0,20);
    setPendingIntents(next);
    persist({pendingIntents:next});
  };

  const removeIntent=(submissionId:string)=>{
    const next=workspaceRef.current.pendingIntents.filter(item=>item.submissionId!==submissionId);
    setPendingIntents(next);
    persist({pendingIntents:next});
  };

  const resolveConfirmedIntent=(intent:SmmPendingIntent,result:SmmCommandResult)=>{
    const displayCode=smmStage5ConfirmedDisplayCode(result.displayCode);
    if(!displayCode){
      const unknown=Object.freeze({...intent,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:'正式訂單已建立，但流水號讀回未完成'});
      saveIntent(unknown);
      setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:'正式結果未完整；只會讀回原本提交，直至取得正式流水號。'}));
      setNotice('正式結果未完整；購物草稿仍然保留，未有建立第二張單。');
      return false;
    }
    removeIntent(intent.submissionId);
    const current=workspaceRef.current;
    if(smmIntentOwnsCart(intent,current.cart)){
      const remaining=smmCartAfterIntent(intent,current.cart),note=remaining.length?current.cartNote:'';
      setCart(remaining);
      setCartNote(note);
      setCheckoutStage(false);
      persist({cart:remaining,cartNote:note});
    }
    setCartOpen(false);
    setStage5Session(Object.freeze({intent,state:'CONFIRMED',displayCode,message:result.message}));
    setNotice(null);
    void refresh();
    return true;
  };

  const releaseSubmitLock=()=>{submitLockRef.current=false;setSubmitting(false);};

  const submitCart=async()=>{
    if(submitLockRef.current||cart.length===0)return;
    submitLockRef.current=true;
    setSubmitting(true);
    if(!menu||publishedTotalMinor===null){
      setNotice('餐單價格／版本未完整，請先重新同步。');
      releaseSubmitLock();
      return;
    }
    if(serviceMode==='DINE_IN'&&!diningTarget){
      setNotice('堂食要先揀掛入邊張枱，或者加入輪候。');
      setDiningTargetOpen(true);
      releaseSubmitLock();
      return;
    }
    if(snapshot?.connectionPath!=='LAN'&&!staffSession){
      setNotice('Internet 員工落單需要先喺「更多 → 員工帳戶」登入一次；之後呢部手機會保持同一個員工帳戶。');
      releaseSubmitLock();
      return;
    }

    const unresolved=pendingIntents.find(item=>(item.state==='PENDING'||item.state==='UNKNOWN'||item.state==='NOT_CONNECTED')&&smmIntentOwnsCart(item,cart));
    if(unresolved){
      if(!port?.readSubmission){
        const unknown=Object.freeze({...unresolved,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:'原提交結果未明'});
        saveIntent(unknown);
        setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:'原提交結果未明；只會確認原本結果。'}));
        releaseSubmitLock();
        return;
      }
      const prior=await port.readSubmission(unresolved.submissionId);
      if(prior.state==='CONFIRMED'){
        resolveConfirmedIntent(unresolved,prior);
        releaseSubmitLock();
        return;
      }
      if(prior.state==='REJECTED'){
        const rejected=Object.freeze({...unresolved,state:'REJECTED' as const,updatedAt:nowIso(),lastMessage:prior.message});
        saveIntent(rejected);
        setStage5Session(Object.freeze({intent:rejected,state:'REJECTED',message:prior.message}));
        releaseSubmitLock();
        return;
      }
      const unknown=Object.freeze({...unresolved,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:prior.message});
      saveIntent(unknown);
      setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:prior.message}));
      setNotice('原提交結果仍未確認；未有再次送出。');
      releaseSubmitLock();
      return;
    }

    const existing=pendingIntents.find(item=>
      item.state==='DRAFT'&&
      item.menuRevision===menu.revision&&
      item.checkout?.serviceMode===serviceMode&&
      (serviceMode==='DINE_IN'||item.checkout?.tender===tender)&&
      item.publishedTotalMinor===publishedTotalMinor&&
      JSON.stringify(item.checkout?.diningTarget??null)===JSON.stringify(diningTarget)&&
      JSON.stringify(item.cart)===JSON.stringify(cart)
    );
    const base=existing??createSmmPendingIntent({
      cart,
      menuRevision:menu.revision,
      publishedTotalMinor,
      serviceMode,
      ...(serviceMode==='TAKEAWAY'?{tender}:{}),
      ...(diningTarget?{diningTarget}:{}),
    });
    saveIntent(base);

    if(!port?.submitOrder){
      setStage5Session(null);
      setCartOpen(true);
      setCheckoutStage(true);
      setNotice('傳輸通道離線；正式訂單未送出。已保留原購物草稿同提交身份，唔會背景重送。');
      releaseSubmitLock();
      return;
    }

    const pending=Object.freeze({...base,state:'PENDING' as const,updatedAt:nowIso(),lastMessage:'正式提交已開始，等待本次提交回覆'});
    setCartOpen(false);
    setNotice(null);
    try{
      const submitAttempt=port.submitOrder(pending);
      saveIntent(pending);
      setStage5Session(Object.freeze({intent:pending,state:'DRAFT',message:'正式提交已開始；等待本次提交回覆。'}));
      const result=await submitAttempt;
      if(result.state==='CONFIRMED'){
        resolveConfirmedIntent(pending,result);
        releaseSubmitLock();
        return;
      }
      if(result.state==='REJECTED'||result.state==='FAILED'){
        const rejected=Object.freeze({...pending,state:'REJECTED' as const,updatedAt:nowIso(),lastMessage:result.message});
        saveIntent(rejected);
        setStage5Session(Object.freeze({intent:rejected,state:'REJECTED',message:result.message}));
        if(result.message.startsWith('SMM_PUBLISHED_PRICE_CHANGED')||result.message.startsWith('SMM_MENU_REVISION_CHANGED')){
          removeIntent(pending.submissionId);
          setStage5Session(null);
          await refresh();
          setCartOpen(true);
          setCheckoutStage(false);
          setNotice('餐單已更新，需要重新確認有變更嘅商品；購物車已保留，系統唔會自動再次提交。');
        }
        releaseSubmitLock();
        return;
      }
      if(result.state==='NOT_CONNECTED'){
        const offlineDraft=Object.freeze({...base,state:'DRAFT' as const,updatedAt:nowIso(),lastMessage:result.message});
        saveIntent(offlineDraft);
        setStage5Session(null);
        setCartOpen(true);
        setCheckoutStage(true);
        setNotice('傳輸通道離線；正式訂單未送出。已保留原購物草稿同提交身份，唔會背景重送。');
        setConnection('NOT_CONNECTED');
        releaseSubmitLock();
        return;
      }
      const unknown=Object.freeze({...pending,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:result.message});
      saveIntent(unknown);
      setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:result.message}));
      releaseSubmitLock();
    }catch{
      const unknown=Object.freeze({...pending,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:'提交結果未明'});
      saveIntent(unknown);
      setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:'提交結果未明；只會確認原本結果。'}));
      releaseSubmitLock();
    }
  };

  const readbackIntent=async(intent:SmmPendingIntent)=>{
    if(stage5Reading)return;
    setStage5Reading(true);
    try{
      if(!port?.readSubmission){
        const unknown=Object.freeze({...intent,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:'門店查詢服務尚未連接'});
        saveIntent(unknown);
        setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:'門店查詢服務尚未連接；未有再次送出。'}));
        return;
      }
      const result=await port.readSubmission(intent.submissionId);
      if(result.state==='CONFIRMED'){
        resolveConfirmedIntent(intent,result);
        return;
      }
      if(result.state==='REJECTED'||result.state==='FAILED'){
        const rejected=Object.freeze({...intent,state:'REJECTED' as const,updatedAt:nowIso(),lastMessage:result.message});
        saveIntent(rejected);
        setStage5Session(Object.freeze({intent:rejected,state:'REJECTED',message:result.message}));
        return;
      }
      if(result.state==='NOT_CONNECTED'){
        const unchangedState=intent.state==='UNKNOWN'?'UNKNOWN':intent.state==='PENDING'?'PENDING':'DRAFT';
        const offlineIntent=Object.freeze({...intent,state:unchangedState as 'DRAFT'|'PENDING'|'UNKNOWN',updatedAt:nowIso(),lastMessage:result.message});
        saveIntent(offlineIntent);
        setStage5Session(Object.freeze({intent:offlineIntent,state:unchangedState,message:'傳輸通道離線；原交易狀態保持不變。'}));
        setConnection('NOT_CONNECTED');
        return;
      }
      const unknown=Object.freeze({...intent,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:result.message});
      saveIntent(unknown);
      setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:result.message}));
    }catch{
      const unknown=Object.freeze({...intent,state:'UNKNOWN' as const,updatedAt:nowIso(),lastMessage:'讀回結果未明'});
      saveIntent(unknown);
      setStage5Session(Object.freeze({intent:unknown,state:'UNKNOWN',message:'讀回結果未明；未有再次送出。'}));
    }finally{
      setStage5Reading(false);
    }
  };

  const repairStage5=()=>{
    const session=stage5Session;
    if(!session||session.state!=='REJECTED')return;
    const repair=smmStage5RepairPath(session.message);
    removeIntent(session.intent.submissionId);
    setStage5Session(null);
    if(repair.target==='STAFF'){
      setCartOpen(false);
      setCheckoutStage(false);
      setView('more');
      setMoreTool('staff');
      return;
    }
    setCartOpen(true);
    setCheckoutStage(repair.target==='CHECKOUT');
  };

  const changeCartNote=(value:string)=>{
    const next=value.slice(0,160);
    setCartNote(next);
    persist({cartNote:next});
  };

  const startNewOrder=()=>{
    const current=workspaceRef.current;
    const nextCart=stage5Session?smmCartAfterIntent(stage5Session.intent,current.cart):current.cart,nextNote=nextCart.length?current.cartNote:'';
    setCart(nextCart);setCartNote(nextNote);setView('order');
    persist({cart:nextCart,cartNote:nextNote,preferences:{activeView:'order',activeCategoryId,sourceFilter,serviceMode,tender,diningTarget}});
    setStage5Session(null);setCartOpen(false);setCheckoutStage(false);
    setNotice('原單保留在「更多 → 未確認嘅單」，可隨時確認結果。');
  };

  const localDraftCount=pendingIntents.length+commandReadbacks.length;
  const operationsConnection=operationalConnection(snapshot,connection);
  const operationsMissing=Boolean(snapshot?.operational&&!snapshot.operational.available&&!snapshot.orders.length);
  const sourceLabel=operationalLabel(snapshot);
  const operationsFailure=snapshot?.operational?.failure;
  const providerChannels=snapshot?.operational?(snapshot.channels??[]).filter(channel=>channel.channel!=='ADMIN_CANONICAL_PROJECTION'&&channel.channel!=='DINING'):snapshot?.channels??[];
  const connectionLabel=!browserOnline?'已離線':connection==='READY'&&sourceLabel?sourceLabel:connection==='READY'?(snapshot?.connectionPath==='LAN'?'LAN 已連接':'Internet 已連接'):connection==='LOADING'?'同步中':connection==='ERROR'?'同步失敗':'門店服務未連接';
  const webSmtAcceptance=typeof window!=='undefined'&&new URLSearchParams(window.location.search).get('target')==='web-smt';
  const queueItems=(snapshot?.work??[]).filter((item,index,rows)=>rows.findIndex(other=>(other.orderId||other.workId)===(item.orderId||item.workId))===index);
  const pendingOrderCount=queueItems.length;
  const ordersIncomplete=snapshot?.operational?.complete===false;
  const compactStatus=operationalStatusLabel(snapshot,connection,browserOnline);

  return <main className="app-shell" data-mode={!browserOnline||connection!=='READY'?'offline':operationsConnection!=='READY'?'degraded':'online'} data-view={view}>
    <header className="topbar">
      {view==='order'?<img className="brand-mark brand-logo-topbar" src="/brand/morefun-logo.webp" alt="磨飯"/>:null}
      <div className="brand-copy"><strong>{view==='order'?'點單':view==='dine'?'桌檯':view==='more'?'更多':'訂單'}</strong></div>
      <button className="state-pill" onClick={()=>setStatusOpen(value=>!value)} aria-label="查看同步狀態" aria-expanded={statusOpen}><i/>{compactStatus}</button>
      {view==='order'?<button type="button" className="ui-search-toggle" aria-label="搜尋商品" aria-expanded={searchOpen} onClick={()=>setSearchOpen(value=>!value)}><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10" cy="10" r="6"/><path d="m15 15 5 5"/></svg></button>:null}
      <button type="button" className="ui-staff-avatar" aria-label="員工帳戶" onClick={()=>{setMoreTool('staff');changeView('more')}}>{(staffSession?.displayName??snapshot?.staff?.displayName??'員').slice(0,1)}</button>
    </header>

    {!browserOnline||connection==='NOT_CONNECTED'?<section className="ui-offline-banner" role="status"><span>連唔到門店，購物車保留住</span><button type="button" onClick={()=>void refresh()}>重試</button></section>:null}

    {statusOpen?<section className="ui-sync-detail" aria-label="同步詳情"><strong>{connectionLabel}</strong><p>{sourceLabel??'門店資料與營運資料分別確認；連線不代表 Pad 即時在線。'}</p><button type="button" onClick={()=>void refresh()} aria-label="重新同步門店資料">重新同步門店資料</button><button type="button" onClick={()=>{setMoreTool('pending');changeView('more');setStatusOpen(false)}}>本機待確認 {localDraftCount}</button></section>:null}

    {notice?<div className="notice" role="status"><span>{notice}</span><button onClick={()=>setNotice(null)}>收起</button></div>:null}
    {webSmtAcceptance?<section className="recovery-banner"><strong>網頁收銀機驗收模式</strong><span>呢個頁面只會將測試訂單送到臨時公網收銀機；唔會送去舖頭實機、唔會觸發實體打印。</span></section>:null}
    {error?<StageXState compact kind="ERROR" title="門店資料暫時未能更新" detail={error} primaryLabel="再試一次" onPrimary={()=>void refresh()}/>:null}
    {connection==='NOT_CONNECTED'?<StageXState compact kind="OFFLINE" title="尚未連接門店服務" detail="本機草稿會保留；連線後再載入餐單、訂單同營運資料。"/>:null}
    {connection==='STALE'?<StageXState compact kind="STALE" onPrimary={()=>void refresh()}/>:null}
    {connection==='PARTIAL'?<StageXState compact kind="PARTIAL" onPrimary={()=>void refresh()}/>:null}
    {connection==='UNKNOWN'?<StageXState compact kind="UNKNOWN" primaryLabel="重新確認" onPrimary={()=>void refresh()}/>:null}

    {operationsFailure&&operationsFailure.kind!=='NOT_READ'?<section className="recovery-banner" role="status" aria-label="營運資料未同步">
      <strong>營運資料未同步</strong><span>{operationalFailureMessage(operationsFailure)}。餐單及購物車仍可使用。</span>
      {operationsFailure.kind==='UNAUTHORIZED'?<button type="button" onClick={()=>{forgetSmmStaffSession();changeStaffSession(null);setMoreTool('staff');changeView('more');}}>重新登入</button>:<button type="button" onClick={()=>void refresh()}>重試營運資料</button>}
    </section>:null}
    <section className="stage">
      {view==='work'||view==='orders'?<div className="ui-orders-tabs" aria-label="訂單工作範圍">
        <button type="button" className={view==='work'?'active':''} onClick={()=>{setOrderTab('PENDING');changeView('work')}}>待處理 {ordersIncomplete?'已載入 ':''}<b>{pendingOrderCount}</b></button>
        <button type="button" className={view==='orders'&&orderTab==='WORKING'?'active':''} onClick={()=>{setOrderTab('WORKING');changeView('orders')}}>製作中</button>
        <button type="button" className={view==='orders'&&orderTab==='READY'?'active':''} onClick={()=>{setOrderTab('READY');changeView('orders')}}>可取餐</button>
        <button type="button" className={view==='orders'&&orderTab==='HISTORY'?'active':''} onClick={()=>{setOrderTab('HISTORY');changeView('orders')}}>全部／歷史</button>
      </div>:null}
      {(view==='work'||view==='orders')&&ordersIncomplete&&!snapshot?.orderPages?<p role="status" aria-label="訂單資料範圍" className="callout">已載入 {snapshot?.orders.length??0} 張，仍有資料未讀回。</p>:null}
      {(view==='work'||view==='orders')&&snapshot?.orderPages?<section className="callout" aria-label="訂單分頁範圍">
        <p>已載入當日待辦 {snapshot.orderPages.active.loadedCount}／歷史 {snapshot.orderPages.history.loadedCount} 張；只顯示已讀回資料。</p>
        {snapshot.orderPages.history.query.from?<p>歷史營業日：{snapshot.orderPages.history.query.from} 至 {snapshot.orderPages.history.query.to}</p>:null}
      </section>:null}
      {(view==='work'||view==='orders')&&pagingError?<section className="recovery-banner" role="alert"><span>{pagingError}</span><button type="button" onClick={()=>void refresh()}>重新整理訂單資料</button></section>:null}
      {view==='order'?<OrderView
        connection={connection}
        categories={categories}
        activeCategoryId={effectiveCategoryId}
        setCategory={changeCategory}
        search={search}
        showSearch={searchOpen}
        setSearch={setSearch}
        cards={visibleCards}
        allCards={menuCards}
        menu={menu}
        cart={cart}
        quote={quote}
        serviceMode={serviceMode}
        diningLabel={smmStage4DiningTargetStatus(serviceMode,diningTarget,snapshot?.diningTables??[]).label}
        onServiceMode={changeServiceMode}
        onChooseTable={()=>{setDineCovers(diningTarget?.covers??dineCovers);setDiningTargetOpen(true)}}
        onQuantity={(lineId,quantity)=>updateCart(cart.map(line=>line.lineId===lineId?{...line,quantity:Math.min(99,Math.max(1,quantity))}:line))}
        onProduct={product=>{setEditingLineId(null);setSelectedProduct(product);setSelections(initialSmmSelections(product.optionGroups));setSelectedVariationId(null);setComboEnabled(false);setComboSelections({});setComboChildren({})}}
        onComboProduct={product=>{setEditingLineId(null);setSelectedProduct(product);setSelections(initialSmmSelections(product.optionGroups));setSelectedVariationId(null);setComboEnabled(true);setComboSelections({});setComboChildren({})}}
        onCart={()=>{setCheckoutStage(false);setCartOpen(true)}}
      />:null}
      {view==='work'?(operationsMissing?<EmptyState title="訂單及工作資料未同步" detail="未收到門店正式資料，暫時無法確認有冇待處理訂單。"><button type="button" onClick={()=>void refresh()}>重試營運資料</button></EmptyState>:<Stage6QueueView connection={operationsConnection} items={queueItems} orders={snapshot?.orders??[]} onRefresh={refresh} commandReadbacks={commandReadbacks} onFulfill={runStoreCommand}/>):null}
      {view==='work'&&snapshot?.orderPages?.active.hasMore?<button type="button" className="stage7-refresh-button" disabled={pagingBusy||connection!=='READY'} onClick={()=>void readOrderPage({...snapshot.orderPages!.active.query,cursor:snapshot.orderPages!.active.nextCursor!})}>{pagingBusy?'載入中…':'載入更多未完成訂單'}</button>:null}
      {view==='orders'?(operationsMissing?<EmptyState title="訂單資料未同步" detail="未收到門店正式資料，唔會當作冇訂單；請重新同步。"><button type="button" onClick={()=>void refresh()}>重試營運資料</button></EmptyState>:<Stage7OrdersView initialTab={orderTab} connection={operationsConnection} rows={snapshot?.orders??[]} incomplete={ordersIncomplete} onRefresh={refresh} paging={snapshot?.orderPages} pagingBusy={pagingBusy||connection!=='READY'} onPage={readOrderPage} onSearchPage={snapshot?.orderPages&&port?.readOrderPage?query=>port.readOrderPage!(query):undefined} onDrinks={port?.readPendingDrinks&&port.fulfillOrder?setDrinkOrderId:undefined} onPayment={port?.readPaymentEvidence&&port.fulfillOrder&&staffSession?(orderId,onReadback)=>setOrderAction({kind:'payment',orderId,onReadback}):undefined} onCancel={port?.fulfillOrder&&staffSession?(orderId,displayCode,onReadback)=>setOrderAction({kind:'cancel',orderId,displayCode,onReadback}):undefined}/>):null}
      {view==='dine'&&snapshot?.operational?.dining==='NOT_CONNECTED'?<EmptyState title="堂食枱位未接駁" detail="目前未有正式枱位資料，唔代表冇枱或冇掛單。仍可從點單頁建立堂食單。"><button type="button" onClick={()=>{changeView('order');changeServiceMode('DINE_IN');}}>去點單頁落堂食單</button></EmptyState>:view==='dine'?<Stage8DineView
        connection={snapshot?.operational?.available===false?'ERROR':connection}
        sessions={snapshot?.dineSessions??[]}
        tables={snapshot?.diningTables??[]}
        onRefresh={refresh}
        onCommand={port?.fulfillOrder?runStoreCommand:undefined}
        commandReadbacks={commandReadbacks}
        onStartOrder={(selected,covers)=>{
          setDineTable(selected.tableId);
          setDineCovers(covers);
          const target:SmmDiningTarget={kind:'TABLE',tableId:selected.tableId,covers};
          changeServiceMode('DINE_IN');
          changeDiningTarget(target);
          setDiningTargetOpen(false);
          setNotice('已選 '+selected.label+'；加入商品後可以送去收銀機處理。');
          changeView('order');
        }}
        onStartWaiting={covers=>{
          setDineCovers(covers);
          changeServiceMode('DINE_IN');
          changeDiningTarget({kind:'WAITING',covers});
          setDiningTargetOpen(false);
          setNotice('已記低輪候人數；完成點單後會交畀收銀機處理。');
          changeView('order');
        }}
      />:null}
      {view==='more'?<Stage9MoreView
        tool={moreTool}
        setTool={setMoreTool}
        statuses={{
          staff:staffSession?.displayName??'未登入',
          pending:String(localDraftCount),
          connection:snapshot?.connectionPath==='LAN'?'LAN':snapshot?.connectionPath==='INTERNET'?'Internet':connectionLabelShort(connection),
          channels:providerChannels.length?providerChannels.length+' 條渠道':port?.readChannels?'查看狀態':snapshot?.operational?'未接駁':'未有資料',
          business:snapshot?.businessDay?.businessDate??snapshot?.orderPages?.active.activeBusinessDate??'未有資料',
          printing:snapshot?.printHealth?.length?snapshot.printHealth.length+' 個打印位置':port?.readPrintHealth?'查看狀態':'未有資料',
          capacity:port?.readCapacity?'查看產能':capacityLabel(snapshot?.capacity?.state),
          reporting:snapshot?.reporting?snapshot.reporting.orderCount+' 單 · '+snapshot.reporting.salesLabel:port?.readReports?'查看報表':reportingFreshnessLabel(snapshot?.reporting?.freshness),
          refunds:snapshot?.operational?'未接駁':snapshot?.refundRequests?.length?snapshot.refundRequests.length+' 項待跟進':'目前冇待跟進',
          diagnostics:connectionLabelShort(connection),
        }}
      >
        {moreTool==='staff'?<StaffLogin session={staffSession} onSession={changeStaffSession}/>:
         moreTool==='connection'?<ConnectionSettings snapshot={snapshot}/>:
         moreTool==='pending'?<PendingIntents intents={pendingIntents} commands={commandReadbacks} onCommandReadback={async input=>{const result=await runStoreCommand(input);setNotice(result.message);if(result.state==='CONFIRMED')void refresh();}} onReadback={intent=>void readbackIntent(intent)} onDiscard={removeIntent}/>:
         moreTool==='channels'?<ChannelList connection={connection} channels={providerChannels} operational={Boolean(snapshot?.operational)} readChannels={port?.readChannels}/>:
         moreTool==='business'?(port?.readBusinessDay?<BusinessDayReceipts read={port.readBusinessDay}/>:<BusinessDay projection={snapshot?.businessDay} activeBusinessDate={snapshot?.orderPages?.active.activeBusinessDate}/>):
         moreTool==='capacity'?<Capacity projection={snapshot?.capacity} readCapacity={port?.readCapacity}/>:
         moreTool==='reporting'?<Reporting projection={snapshot?.reporting} readReports={port?.readReports}/>:
         moreTool==='refunds'?(snapshot?.operational?<EmptyState title="退款要求未接駁" detail="目前只讀取正式訂單資料，不能判斷有冇退款待跟進。"/>:<RefundRequests connection={connection} rows={snapshot?.refundRequests??[]}/>):
         moreTool==='printing'?<PrintHealth connection={connection} rows={snapshot?.printHealth??[]} readPrintHealth={snapshot?.connectionPath==='LAN'?undefined:port?.readPrintHealth} onRefresh={snapshot?.connectionPath==='LAN'?refresh:undefined}/>:
         moreTool==='diagnostics'?<><Diagnostics connection={operationsConnection} snapshot={snapshot} pendingCount={pendingIntents.length}/><SyncDiagnostics/></>:null}
      </Stage9MoreView>:null}
    </section>

    <nav className="bottom-nav" aria-label="主要功能">
      <NavButton active={view==='order'} label="點單" onClick={()=>changeView('order')}/>
      <NavButton active={view==='orders'||view==='work'} label="訂單" badge={pendingOrderCount?String(pendingOrderCount):undefined} onClick={()=>changeView('work')}/>
      <NavButton active={view==='dine'} label="桌檯" onClick={()=>changeView('dine')}/>
      <NavButton active={view==='more'} label="更多" badge={localDraftCount?String(localDraftCount):undefined} onClick={()=>changeView('more')}/>
    </nav>

    {selectedProduct?<ProductSheet
      product={selectedProduct}
      family={comboEnabled&&selectedProduct.comboId&&!selectedProduct.mainMeal?undefined:selectedFamily}
      onFamilyProduct={changeFamilyProduct}
      menu={menu}
      serviceMode={serviceMode}
      editing={Boolean(editingLineId)}
      initialQuantity={cart.find(line=>line.lineId===editingLineId)?.quantity??1}
      selections={selections}
      selectedVariationId={selectedVariationId}
      comboEnabled={comboEnabled}
      comboSelections={comboSelections}
      comboChildren={comboChildren}
      toggleChild={(choice,groupId,optionId)=>{const group=choice.optionGroups.find(g=>g.optionGroupId===groupId);if(group)setComboChildren(current=>({...current,[choice.key]:toggleSmmSelection(current[choice.key]??{},group,optionId)}));}}
      setVariation={setSelectedVariationId}
      setComboEnabled={value=>{setComboEnabled(value);if(!value){setComboSelections({});setComboChildren({});}}}
      toggle={(groupId,optionId)=>{
        const group=selectedProduct.optionGroups.find(item=>item.optionGroupId===groupId);
        if(group)setSelections(current=>toggleSmmSelection(current,group,optionId));
      }}
      toggleCombo={(poolId,groupId,choiceId)=>{
        const resolved=resolveSmmProductCombo(selectedProduct,menu);
        const group=resolved?.groups.find(item=>item.pool.poolId===poolId&&item.group.groupId===groupId);
        if(group){const next=changeSmmComboChoice(comboSelections,comboChildren,group,choiceId);setComboSelections(next.selections);setComboChildren(next.children);}
      }}
      onClose={()=>{const returnToCart=Boolean(editingLineId);resetProductEditor();if(returnToCart){setCheckoutStage(false);setCartOpen(true)}}}
      onAdd={addSelectedProduct}
    />:null}

    {cartOpen?<CartSheet
      cart={cart}
      quote={quote}
      menu={menu}
      checkoutStage={checkoutStage}
      serviceMode={serviceMode}
      tender={tender}
      diningTarget={diningTarget}
      diningTables={snapshot?.diningTables??[]}
      note={cartNote}
      onNote={changeCartNote}
      onServiceMode={changeServiceMode}
      onTender={changeTender}
      onChooseDiningTarget={()=>{setDineCovers(diningTarget?.covers??dineCovers);setDiningTargetOpen(true)}}
      onClose={()=>{setCartOpen(false);setCheckoutStage(false)}}
      onBackToCart={()=>setCheckoutStage(false)}
      onEdit={openCartLineEditor}
      onAcceptRefresh={acceptCartRefresh}
      onQuantity={(lineId,quantity)=>updateCart(cart.map(line=>line.lineId===lineId?{...line,quantity:Math.min(99,Math.max(1,quantity))}:line))}
      onRemove={lineId=>updateCart(cart.filter(line=>line.lineId!==lineId))}
      onClear={()=>{setCart([]);setCartNote('');setCheckoutStage(false);persist({cart:[],cartNote:''})}}
      onCheckout={()=>setCheckoutStage(true)}
      onSubmitBoundary={()=>void submitCart()}
    />:null}

    {stage5Session?<Stage5SubmitView
      session={stage5Session}
      diningLabel={snapshot?.diningTables?.find(table=>table.tableId===stage5Session.intent.checkout.diningTarget?.tableId)?.label}
      submitting={submitting}
      reading={stage5Reading}
      connection={connection}
      onReadback={()=>void readbackIntent(stage5Session.intent)}
      onRepair={repairStage5}
      onBack={()=>{setStage5Session(null);setCartOpen(false);setCheckoutStage(false);changeView('more');setMoreTool('pending')}}
      onViewOrder={()=>{setStage5Session(null);setCartOpen(false);setCheckoutStage(false);changeView('orders')}}
      onContinue={()=>{setStage5Session(null);setCartOpen(false);setCheckoutStage(false);changeView('order')}}
      onNewOrder={startNewOrder}
      onPendingDetails={()=>{setStage5Session(null);setCartOpen(false);setCheckoutStage(false);changeView('more');setMoreTool('pending')}}
      onHome={()=>{setStage5Session(null);setCartOpen(false);setCheckoutStage(false);changeView('order')}}
    />:null}

    {drinkOrderId&&port?.readPendingDrinks&&port.fulfillOrder?<PendingDrinkSheet key={drinkOrderId} orderId={drinkOrderId} read={port.readPendingDrinks} onCommand={runStoreCommand} pending={commandReadbacks.find(item=>item.input.orderId===drinkOrderId)} onClose={()=>setDrinkOrderId(null)} onSaved={message=>{setNotice(message);setDrinkOrderId(null);void refresh();}}/>:null}
    {orderAction?.kind==='payment'&&port?.readPaymentEvidence&&port.fulfillOrder?<PaymentReviewSheet key={orderAction.orderId} orderId={orderAction.orderId} read={port.readPaymentEvidence} onCommand={runStoreCommand} pending={commandReadbacks.find(item=>item.input.orderId===orderAction.orderId)} onClose={()=>setOrderAction(null)} onSaved={message=>{setNotice(message);orderAction.onReadback();setOrderAction(null);}}/>:null}
    {orderAction?.kind==='cancel'&&port?.fulfillOrder?<CancelOrderSheet key={orderAction.orderId} orderId={orderAction.orderId} displayCode={orderAction.displayCode} onCommand={runStoreCommand} pending={commandReadbacks.find(item=>item.input.orderId===orderAction.orderId)} onClose={()=>setOrderAction(null)} onSaved={message=>{setNotice(message);orderAction.onReadback();setOrderAction(null);}}/>:null}
    {diningTargetOpen?<DiningTargetSheet
      tables={snapshot?.diningTables??[]}
      covers={dineCovers}
      setCovers={setDineCovers}
      onClose={()=>setDiningTargetOpen(false)}
      onSelect={target=>{changeDiningTarget(target);setDiningTargetOpen(false);const label=(snapshot?.diningTables??[]).find(row=>row.tableId===target.tableId)?.label;setNotice(target.kind==='TABLE'?'堂食會掛入 '+(label??target.tableId)+'。':'堂食會先加入輪候。');}}
    />:null}
  </main>;
}

export function OrderView({connection,categories,activeCategoryId,setCategory,search,showSearch=false,setSearch,cards,allCards=cards,menu,cart,quote,serviceMode,diningLabel,onServiceMode,onChooseTable,onQuantity,onProduct,onComboProduct,onCart}:{
  connection:SmmConnectionState;
  categories:readonly {categoryId:string;name:string}[];
  activeCategoryId:string|null;
  setCategory:(v:string|null)=>void;
  search:string;
  showSearch?:boolean;
  setSearch:(v:string)=>void;
  cards:readonly SmmMenuCard[];
  allCards?:readonly SmmMenuCard[];
  menu:SmmReadModelSnapshot['menu'];
  cart:readonly SmmCartLine[];
  quote:SmmQuoteSnapshot|null;
  serviceMode:SmmServiceMode;
  diningLabel:string;
  onServiceMode:(mode:SmmServiceMode)=>void;
  onChooseTable:()=>void;
  onQuantity:(id:string,quantity:number)=>void;
  onProduct:(p:SmmProduct)=>void;
  onComboProduct:(p:SmmProduct)=>void;
  onCart:()=>void;
}){
  const count=cart.reduce((sum,line)=>sum+line.quantity,0);
  const unavailable=connection!=='READY';
  const [mainComboId,setMainComboId]=useState<string|null>(null);
  const comboCards=smmComboCards(menu);
  const mainCard=comboCards.find(card=>card.combo.comboId===mainComboId);
  const query=search.trim().toLocaleLowerCase();
  const visibleCombos=comboCards.filter(card=>(Boolean(query)||card.mains.some(product=>product.categoryId===activeCategoryId))&&
    (!query||card.combo.name.toLocaleLowerCase().includes(query)||card.mains.some(product=>product.name.toLocaleLowerCase().includes(query))));
  return <>{mainCard?<FocusDialog onClose={()=>setMainComboId(null)}><div className="overlay stage2-overlay"><section className="sheet stage2-product-sheet" role="dialog" aria-modal="true" aria-label="選擇套餐主食">
    <header className="stage2-sheet-header"><div><span>選擇套餐主食</span><h2>{mainCard.combo.name}</h2><small>{money('HKD',mainCard.combo.publishedBasePriceMinor)} · 主食選好後再揀套餐內容</small></div><button type="button" onClick={()=>setMainComboId(null)} aria-label="關閉套餐主食選擇">✕</button></header>
    <div className="stage2-scroll-body stage2-option-grid">{mainCard.mains.map(product=><button type="button" key={product.productId} disabled={unavailable} onClick={()=>{onComboProduct(product);setMainComboId(null)}}><span>{product.name}</span></button>)}</div>
  </section></div></FocusDialog>:null}<section className="page order-page stage1-order">
    <div className="ui-service-switch" aria-label="點單服務方式">
      <button type="button" className={serviceMode==='DINE_IN'?'active':''} aria-pressed={serviceMode==='DINE_IN'} onClick={()=>{onServiceMode('DINE_IN');onChooseTable()}}>堂食 <span>{serviceMode==='DINE_IN'?diningLabel:'選枱'}</span></button>
      <button type="button" className={serviceMode==='TAKEAWAY'?'active':''} aria-pressed={serviceMode==='TAKEAWAY'} onClick={()=>onServiceMode('TAKEAWAY')}>外賣</button>
    </div>

    {showSearch||search?<label className="search stage1-search">
      <span aria-hidden="true">⌕</span>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="搜尋商品" aria-label="搜尋商品"/>
      {search?<button type="button" onClick={()=>setSearch('')} aria-label="清除搜尋">×</button>:null}
    </label>:null}

    {categories.length?<div className="category-rail stage1-category-rail" aria-label="商品分類">
      {categories.map(item=><button key={item.categoryId} className={activeCategoryId===item.categoryId?'active':''} onClick={()=>setCategory(item.categoryId)}><b>{item.name}</b><small>{allCards.filter(card=>card.categoryId===item.categoryId).length} 款</small></button>)}
    </div>:null}

    <div className="stage1-section-title">
      <div><span>{search?'搜尋結果':categories.find(row=>row.categoryId===activeCategoryId)?.name??'餐點'}</span><small>{unavailable?'餐單同步中':cards.length+' 項'}</small></div>
    </div>

    {connection==='LOADING'?<EmptyState title="正在更新餐單" detail="餐單準備好之後，商品會自動顯示。"/>:
      !categories.length?<EmptyState title={connection==='READY'?'暫時未有餐單':'餐單暫時未能載入'} detail={connection==='READY'?'目前未有可供選擇嘅商品。':'請稍後重新整理，已揀內容唔會因為重新連線而重複落單。'}/>:
      cards.length?<div className="product-grid stage1-product-grid">{cards.map(card=>{
        const product=card.product,price=card.priceMinor,priceLabel=Number.isSafeInteger(Number(price))?money('HKD',Number(price))+(card.priceFrom?' 起':''):'價格待更新';
        const lines=cart.filter(line=>card.family?card.family.members.some(member=>member.productId===line.productId):line.productId===product.productId);
        const selectedCount=lines.reduce((sum,line)=>sum+line.quantity,0);
        return <article key={product.productId} className={`product-card stage1-product-card ${product.available?'':'disabled'}`}>
          <button type="button" className="ui-product-open" disabled={!product.available} onClick={()=>onProduct(product)} aria-label={card.name+' '+priceLabel}>
          {!product.available?<span className="stage1-soldout">暫停供應</span>:null}
          <span className="stage1-product-copy">
            <strong>{card.name}</strong>
          </span>
          <strong className="ui-product-price">{priceLabel}</strong>
          </button>
          <div className="ui-product-bottom">
            {product.available?<button type="button" className="ui-product-options" onClick={()=>onProduct(product)} aria-label={'選項 '+card.name}>選項</button>:null}
            {product.available?(lines.length===1?<div className="ui-product-stepper" aria-label={product.name+' 已選數量'}>
              <button type="button" disabled={lines[0].quantity<=1||Boolean(lines[0].refreshAttention)} onClick={()=>onQuantity(lines[0].lineId,lines[0].quantity-1)} aria-label={'減少 '+product.name}>−</button><b>{selectedCount}</b><button type="button" disabled={lines[0].quantity>=99||Boolean(lines[0].refreshAttention)} onClick={()=>onQuantity(lines[0].lineId,lines[0].quantity+1)} aria-label={'增加 '+product.name}>＋</button>
            </div>:lines.length>1?<button type="button" className="ui-multiple-lines" onClick={onCart}>已選 {selectedCount}</button>:<button type="button" className="stage1-product-add" onClick={()=>onProduct(product)} aria-label={'客製 '+product.name}>＋ 加一份</button>):null}
          </div>
        </article>;
      })}</div>:
      <section className="stage1-zero-result">
        <img src="/brand/stage0/stage0-female.webp" alt="" aria-hidden="true"/>
        <h2>搵唔到呢款商品</h2>
        <button className="primary" onClick={()=>setSearch('')}>清除搜尋</button>
      </section>}

    {connection==='READY'&&visibleCombos.length?<section aria-label="套餐選擇">
      <div className="stage1-section-title"><div><span>套餐</span></div></div>
      <div className="product-grid stage1-product-grid">{visibleCombos.map(card=><article className="product-card stage1-product-card" key={card.combo.comboId}>
        <button type="button" className="ui-product-open" aria-label={card.combo.name+' 選擇主食'} onClick={()=>setMainComboId(card.combo.comboId)}><span className="stage1-product-copy"><strong>{card.combo.name}</strong><i>{card.mains.length} 款主食可選</i></span><strong className="ui-product-price">{money('HKD',card.combo.publishedBasePriceMinor)}</strong></button>
        <div className="ui-product-bottom"><button className="ui-product-options" type="button" onClick={()=>setMainComboId(card.combo.comboId)}>選項</button><button className="stage1-product-add" type="button" onClick={()=>setMainComboId(card.combo.comboId)} aria-label={'選擇 '+card.combo.name}>＋ 加一份</button></div>
      </article>)}</div>
    </section>:null}

    {count>0?<button className="cart-bar stage1-cart-bar" onClick={onCart}>
      <div className="stage1-cart-count"><b>{count}</b><span>件商品</span></div>
      <div><strong>{quote?money(quote.currency,quote.totalMinor):'價格更新中'}</strong></div>
      <em>{connection==='READY'?'查看購物車':'連返先落單'}</em>
    </button>:null}
  </section></>;
}

function StaffLogin({session,onSession}:{session:SmmStaffSession|null;onSession:(session:SmmStaffSession|null)=>void}){
  const [staff,setStaff]=useState<readonly SmmStaffDirectoryItem[]>([]);
  const [staffId,setStaffId]=useState(session?.loginId??'');
  const [pin,setPin]=useState('');
  const [state,setState]=useState(session?'已登入：'+session.displayName:'揀員工，再輸入 PIN。');
  const [busy,setBusy]=useState(false);

  useEffect(()=>{
    let cancelled=false;
    void listSmmStaff().then(rows=>{
      if(cancelled)return;
      setStaff(rows);
      if(!staffId&&rows[0])setStaffId(rows[0].loginId||rows[0].staffId);
    }).catch(()=>{if(!cancelled)setState('暫時未能讀取員工名單。');});
    return()=>{cancelled=true};
  },[]);

  if(session)return <section className="panel staff-login"><h2>{session.displayName}</h2><p>已登入{session.loginId?' · '+session.loginId:''}</p><p>交班後登入下一位員工。購物車及未確認單會保留在此手機。</p><button className="danger" onClick={()=>{clearSmmStaffSession();onSession(null);setPin('');setState('請選擇下一位員工，再輸入 PIN。');}}>登出／切換帳戶</button></section>;

  return <section className="panel staff-login"><p>{state}</p>
    {staff.length?<div className="ui-staff-picker" aria-label="選擇店員">{staff.map(item=><button key={item.staffId} type="button" aria-pressed={staffId===(item.loginId||item.staffId)} className={staffId===(item.loginId||item.staffId)?'active':''} onClick={()=>{setStaffId(item.loginId||item.staffId);setPin('')}}><b aria-hidden="true">{item.displayName.slice(0,1)}</b><span>{item.displayName}</span></button>)}</div>:<label>登入編號<input autoComplete="username" value={staffId} onChange={e=>setStaffId(e.target.value)}/></label>}
    <label>PIN<input type="password" inputMode="numeric" value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,'').slice(0,8))} placeholder="4–8 位數字"/></label>
    <PinKeypad value={pin} onChange={setPin} disabled={busy}/>
    <button className="primary" disabled={busy||!staffId||pin.length<4} onClick={async()=>{
      if(busy)return;
      setBusy(true);setState('驗證中…');
      try{const next=await verifySmmStaff(staffId,pin);onSession(next);setPin('');setState('已登入：'+next.displayName);}
      catch(error){setState(error instanceof Error&&error.message.includes('STAFF_CLOUD_PIN_RESET_REQUIRED')?'此帳號未啟用手機登入。請在 Admin 員工設定重新輸入並保存同一 PIN。':'登入編號或 PIN 未能驗證，請確認後再試。');}
      finally{setBusy(false);}
    }}>{busy?'驗證中…':'登入'}</button>
  </section>;
}

function PendingIntents({intents,commands=[],onCommandReadback,onReadback,onDiscard}:{intents:readonly SmmPendingIntent[];commands?:readonly SmmCommandReadback[];onCommandReadback?:(input:SmmStoreCommandInput)=>Promise<void>;onReadback:(i:SmmPendingIntent)=>void;onDiscard:(id:string)=>void}){
  const [reading,setReading]=useState<string|null>(null);
  if(!intents.length&&!commands.length)return <EmptyState title="冇未確認項目" detail="未確認的訂單及餐枱操作會顯示在這裡。"/>;
  return <>{commands.map(item=><div className="list-row" key={item.commandId}><div><strong>門店操作等待確認</strong><small>參考 {smmStage5SubmissionShortRef(item.commandId)}</small></div><button disabled={Boolean(reading)||!onCommandReadback} onClick={async()=>{setReading(item.commandId);try{await onCommandReadback?.(item.input);}finally{setReading(null);}}}>{reading===item.commandId?'正在確認…':item.retryReady?'以原編號再試':'確認原操作結果'}</button></div>)}{intents.map(intent=>{
    const locked=intent.state==='UNKNOWN'||intent.state==='PENDING';
    const stateLabel=intent.state==='UNKNOWN'?'結果未明':intent.state==='PENDING'?'等待確認':intent.state==='REJECTED'?'已拒絕':intent.state==='NOT_CONNECTED'?'未連接':'待提交';
    return <div className="list-row" key={intent.submissionId}><div><strong>{stateLabel}</strong><small>{intent.cart.length} 項 · 提交參考 {smmStage5SubmissionShortRef(intent.submissionId)}</small><small>{intent.lastMessage??'本機草稿，未代表正式訂單'}</small></div><div className="row-actions"><button onClick={()=>onReadback(intent)}>{intent.state==='UNKNOWN'?'重新確認結果':'確認結果'}</button>{!locked?<button className="danger" onClick={()=>onDiscard(intent.submissionId)}>刪除草稿</button>:null}</div></div>;
  })}</>;
}

function ChannelList({connection,channels,operational=false,readChannels}:{connection:SmmConnectionState;channels:NonNullable<SmmReadModelSnapshot['channels']>;operational?:boolean;readChannels?:SmmRuntimePort['readChannels']}){
  const [saved,setSaved]=useState<readonly SmmReadModelSnapshot['channels'][number][]|null>(null),[busy,setBusy]=useState(false),[error,setError]=useState<string|null>(null);
  const load=async()=>{if(!readChannels||busy)return;setBusy(true);setError(null);try{setSaved(await readChannels());}catch(reason){setError(reason instanceof Error&&reason.message!=='SMM_READ_TIMEOUT'?reason.message:'平台狀態未有及時回應，請重新讀取。');}finally{setBusy(false);}};
  useEffect(()=>{if(readChannels)void load();},[readChannels]);
  const rows=saved??channels;
  return <>
    {readChannels?<button className="primary smm-more-read" disabled={busy} onClick={()=>void load()}>{busy?'讀取中…':'重新讀取'}</button>:null}
    {error?<p className="callout" role="alert">{error}{saved?' 以下保留上次讀回資料。':''}</p>:null}
    {rows.length?rows.map(item=><div className="list-row" key={item.channel}><div><strong>{item.channel}</strong><small>{item.detail}</small>{item.observedAt?<small>來源：{new Date(item.observedAt).toLocaleString('zh-HK')}</small>:null}</div><span className={`status ${item.state==='CONNECTED'?'positive':item.state==='UNKNOWN'?'unknown':'warning'}`}>{readChannels?item.state==='CONNECTED'?'已讀回':item.state==='STALE'?'資料較舊':'未有讀回':channelStateLabel(item.state)}</span></div>):!busy?<EmptyState title={operational?'外賣平台狀態未接駁':connection==='NOT_CONNECTED'?'平台狀態尚未連接':'暫時冇平台狀態'} detail={operational?'目前未有 Keeta／Foodpanda 平台狀態讀回。':'請重新整理渠道狀態。'}/>:null}
  </>;
}

function BusinessDay({projection,activeBusinessDate}:{projection:SmmReadModelSnapshot['businessDay'];activeBusinessDate?:string}){
  if(!projection)return activeBusinessDate?<><div className="list-row"><strong>營業日</strong><strong>{activeBusinessDate}</strong></div><p className="callout">按門店設定的截數時間計算。收舖記錄尚未接駁，請在 Pad 核對。</p></>:<EmptyState title="營業日資料尚未連接" detail="連接後會顯示今日營業日記錄。"/>;
  return <><div className="metric-grid"><Metric label="營業日" value={projection.businessDate}/><Metric label="狀態" value={businessDayStateLabel(projection.state)}/><Metric label="讀取時間" value={new Date(projection.observedAt).toLocaleString('zh-HK')}/></div><p className="callout">營業日用作當日記錄同報表分類。</p></>;
}

function Capacity({projection,readCapacity}:{projection:SmmReadModelSnapshot['capacity'];readCapacity?:SmmRuntimePort['readCapacity']}){
  const [data,setData]=useState<Awaited<ReturnType<NonNullable<SmmRuntimePort['readCapacity']>>>|null>(null),[busy,setBusy]=useState(false),[error,setError]=useState<string|null>(null);
  const load=async()=>{if(!readCapacity||busy)return;setBusy(true);setError(null);try{setData(await readCapacity());}catch(reason){setError(reason instanceof Error&&reason.message!=='SMM_READ_TIMEOUT'?reason.message:'產能未有及時回應，請重新讀取。');}finally{setBusy(false);}};
  useEffect(()=>{if(readCapacity)void load();},[readCapacity]);
  if(!readCapacity)return !projection?<EmptyState title="產能資料尚未連接" detail="未收到門店產能資料，請在 Pad 核對。"/>:<><div className="metric-grid"><Metric label="狀態" value={capacityLabel(projection.state)}/><Metric label="門店提示" value={projection.label}/></div><p className="callout">{projection.detail}</p><small>讀取：{new Date(projection.observedAt).toLocaleString('zh-HK')}</small></>;
  const snapshot=data?.snapshot,current=snapshot&&snapshot.businessDate===data?.businessDate;
  return <>
    <button className="primary smm-more-read" disabled={busy} onClick={()=>void load()}>{busy?'讀取中…':'重新讀取'}</button>
    {error?<p className="callout" role="alert">{error}{data?' 以下保留上次讀回資料。':''}</p>:null}
    {data?<>
      <p>營業日：{data.businessDate}</p>
      {!snapshot?<EmptyState title="未有 Pad 產能讀回" detail="未有資料，不能判斷剩餘份數；請在 Pad 核對。"/>:!current?<EmptyState title="今日產能未同步" detail={`上次資料屬於 ${snapshot.businessDate}，不能當作今日份數。`}/>:!snapshot.pools.length?<EmptyState title="未有產能池資料" detail="請在 Admin 設定並由 Pad 套用。"/>:snapshot.pools.map(pool=><section className="smm-capacity-pool" key={pool.poolId}>
        <h3>{pool.name}</h3>
        <div className="metric-grid"><Metric label="開日份數" value={String(pool.dailyQty)}/><Metric label="今日總額度" value={String(pool.todayTotal)}/><Metric label="剩餘份數" value={String(pool.remainingQty)}/></div>
        <p>自家渠道：{pool.firstPartyAccepting?'接單':'暫停'} · 剩 {pool.firstPartyStopAt} 停</p><p>第三方：{pool.thirdPartyAccepting?'接單':'暫停'} · 剩 {pool.thirdPartyStopAt} 停</p>
      </section>)}
      {snapshot?.invalidActivePoolIds.length?<p className="callout">部分產能設定未完整，請在 Pad 核對。</p>:null}
      {snapshot?<small>Pad 來源：{snapshot.businessDate} · {new Date(snapshot.observedAt).toLocaleString('zh-HK')}</small>:null}
      <p className="callout">總額度包含補貨，並非銷量。渠道狀態只反映各產能池上次讀回，並非全店連線狀態。</p>
    </>:busy?<p role="status">正在讀取產能…</p>:null}
  </>;
}

function Reporting({projection,readReports}:{projection:SmmReadModelSnapshot['reporting'];readReports?:SmmRuntimePort['readReports']}){
  const [from,setFrom]=useState(''),[to,setTo]=useState('');
  const [data,setData]=useState<Awaited<ReturnType<NonNullable<SmmRuntimePort['readReports']>>>|null>(null);
  const [busy,setBusy]=useState(false),[error,setError]=useState<string|null>(null);
  const load=async()=>{
    if(!readReports||busy)return;setBusy(true);setError(null);
    try{const next=await readReports({...(from?{from}:{}),...(to?{to}:{})});setData(next);setFrom(next.from);setTo(next.to);}
    catch(reason){setError(reason instanceof Error&&reason.message!=='SMM_READ_TIMEOUT'?reason.message:'報表未有及時回應，請重新讀取。');}
    finally{setBusy(false);}
  };
  useEffect(()=>{if(readReports)void load();},[readReports]);
  if(!readReports)return !projection?<EmptyState title="營運報表尚未連接" detail="連線後顯示今日數字。"/>:<><div className="metric-grid"><Metric label="營業日" value={projection.businessDate}/><Metric label="訂單" value={String(projection.orderCount)}/><Metric label="營業額" value={projection.salesLabel}/><Metric label="平均單" value={projection.averageOrderLabel}/></div><p className="callout">資料狀態：{reportingFreshnessLabel(projection.freshness)} · {new Date(projection.observedAt).toLocaleString('zh-HK')}</p></>;
  const total=data?.days.reduce((sum,day)=>({gross:sum.gross+day.grossMinor,refund:sum.refund+day.refundMinor,net:sum.net+day.netMinor,orders:sum.orders+day.orders}),{gross:0,refund:0,net:0,orders:0});
  return <>
    <form className="smm-report-query" onSubmit={event=>{event.preventDefault();void load();}}>
      <label>由<input type="date" aria-label="報表開始日期" value={from} disabled={busy} onChange={event=>setFrom(event.target.value)}/></label>
      <label>至<input type="date" aria-label="報表結束日期" value={to} disabled={busy} onChange={event=>setTo(event.target.value)}/></label>
      <button type="submit" className="primary" disabled={busy}>{busy?'讀取中…':'查詢報表'}</button>
    </form>
    {error?<p className="callout" role="alert">{error}{data?' 以下保留上次讀回資料。':''}</p>:null}
    {data?<>
      <p>營業日：{data.from===data.to?data.from:data.from+' 至 '+data.to}</p>
      {data.days.length&&total?<>
        <div className="metric-grid smm-report-metrics"><Metric label="營業額" value={money('HKD',total.gross)}/><Metric label="退款" value={money('HKD',total.refund)}/><Metric label="淨額" value={money('HKD',total.net)}/><Metric label="訂單" value={String(total.orders)}/></div>
        {data.days.map(day=><div className="list-row" key={day.date}><div><strong>{day.date}</strong><small>{day.orders} 單 · 退款 {money('HKD',day.refundMinor)}</small></div><strong>{money('HKD',day.netMinor)}</strong></div>)}
      </>:<EmptyState title="呢段日期未有報表資料" detail="未收到已同步交易或日結紀錄。"/>}
      <small>已同步紀錄 · 讀取於 {new Date(data.observedAt).toLocaleString('zh-HK')}</small>
    </>:busy?<p role="status">正在讀取報表…</p>:null}
  </>;
}

function RefundRequests({connection,rows}:{connection:SmmConnectionState;rows:NonNullable<SmmReadModelSnapshot['refundRequests']>}){
  if(!rows.length)return <EmptyState title={connection==='NOT_CONNECTED'?'退款要求尚未連接':'暫時冇退款要求'} detail="有退款或售後事項時會喺呢度顯示，實際處理請按店舖流程完成。"/>;
  return <>{rows.map(row=><div className="list-row" key={row.refundId}><div><strong>{row.displayCode} · {row.source}</strong><small>{row.reason}{row.amountLabel?` · ${row.amountLabel}`:''}</small></div><span className={`status ${row.state==='UNKNOWN'?'unknown':row.state==='RESOLVED'?'positive':'warning'}`}>{refundStateLabel(row.state)}</span></div>)}</>;
}

function PrintHealth({connection,rows,readPrintHealth,onRefresh}:{connection:SmmConnectionState;rows:NonNullable<SmmReadModelSnapshot['printHealth']>;readPrintHealth?:SmmRuntimePort['readPrintHealth'];onRefresh?:()=>Promise<void>}){
  const [data,setData]=useState<Awaited<ReturnType<NonNullable<SmmRuntimePort['readPrintHealth']>>>|undefined>(undefined),[busy,setBusy]=useState(false),[error,setError]=useState<string|null>(null);
  const load=async()=>{if(busy)return;setBusy(true);setError(null);try{if(readPrintHealth)setData(await readPrintHealth());else await onRefresh?.();}catch(reason){setError(reason instanceof Error&&reason.message==='請重新登入員工帳戶。'?reason.message:'暫時未能讀取打印狀態；已讀回資料保留，請再試。');}finally{setBusy(false);}};
  useEffect(()=>{if(readPrintHealth)void load();},[readPrintHealth]);
  const shown=data?.printHealth??(readPrintHealth?[]:rows);
  return <>
    {readPrintHealth||onRefresh?<button className="primary smm-more-read" disabled={busy} onClick={()=>void load()}>{busy?'讀取中…':'重新讀取'}</button>:null}
    {error?<p className="callout" role="alert">{error}</p>:null}
    {shown.map(row=><section className="smm-print-health" key={row.logicalPrinterId}>
      <div className="list-row"><div><strong>{row.label}</strong><small>{row.detail}</small></div><span className={`status ${row.state==='READY'?'positive':row.state==='UNKNOWN'?'unknown':'warning'}`}>{printStateLabel(row.state)}</span></div>
      {row.probeObservedAt?<small>連線紀錄：{new Date(row.probeObservedAt).toLocaleString('zh-HK')}</small>:null}
      {row.lastDispatch?<p>最後送出：{row.lastDispatch.outcome==='WRITTEN'?'已寫出':row.lastDispatch.outcome==='FAILED'?'未能送出':'結果未明'} · {new Date(row.lastDispatch.observedAt).toLocaleString('zh-HK')}</p>:<p>未有送出紀錄</p>}
      <small>未確認出紙，請在現場核對。</small>
    </section>)}
    {!shown.length&&!busy&&(!readPrintHealth||data!==undefined)?<EmptyState title={data===null?'打印狀態尚未有 Pad 讀回':readPrintHealth&&data?'未有打印機設定':connection==='NOT_CONNECTED'?'列印狀態尚未連接':'暫時冇列印設備資料'} detail="未有資料並不代表打印正常，請在 Pad 核對。"/>:null}
    {data?<p className="callout">已同步記錄 · {new Date(data.observedAt).toLocaleString('zh-HK')}。連線及送出紀錄不代表實際出紙。</p>:null}
  </>;
}

function Diagnostics({connection,snapshot,pendingCount}:{connection:SmmConnectionState;snapshot:SmmReadModelSnapshot|null;pendingCount:number}){
  return <><div className="diag-line"><span>門店連線</span><b className={connection==='READY'?'':'warn'}>{connectionLabelShort(connection)}</b><small>{snapshot?.connectionPath??'—'} · {snapshot?.observedAt?new Date(snapshot.observedAt).toLocaleString('zh-HK'):'未有讀回'}</small></div><div className="diag-line"><span>餐單版本</span><b>{snapshot?.menu?.revision??'—'}</b><small>{snapshot?.menu?.observedAt?new Date(snapshot.menu.observedAt).toLocaleString('zh-HK'):'未有餐單'}</small></div><div className="diag-line"><span>本機待提交</span><b>{pendingCount}</b><small>只係本機草稿，不係正式訂單</small></div></>;
}

function ComboChildOptions({choices,children,toggle}:{choices:readonly SmmResolvedComboChoice[];children:SmmComboChildSelectionState;toggle:(choice:SmmResolvedComboChoice,groupId:string,optionId:string)=>void}){
  return <>{choices.flatMap(choice=>choice.optionGroups.map(group=>{
    const selected=children[choice.key]?.[group.optionGroupId]??[],min=Math.max(group.required?1:0,group.minSelections);
    return <section className="stage2-config-section" key={JSON.stringify([choice.key,group.optionGroupId])} aria-label={choice.choiceLabel+'／'+group.name}>
      <div className="stage2-section-head"><strong>{choice.choiceLabel}／{group.name}</strong><small>{min>0?'必選':'可選'} · 最少 {min} · 最多 {group.maxSelections}</small></div>
      <div className="stage2-option-grid">{group.options.map(option=>{const active=selected.includes(option.optionId);return <button key={option.optionId} type="button" aria-pressed={active} className={active?'active':''} disabled={!option.available||group.maxSelections>1&&selected.length>=group.maxSelections&&!active} onClick={()=>toggle(choice,group.optionGroupId,option.optionId)}>{frontlineOptionName(option)}{publishedAdjustmentLabel(option.publishedAdjustmentMinor)}</button>;})}</div>
    </section>;
  }))}</>;
}

function ProductSheet({
  product,
  family,
  onFamilyProduct,
  menu,
  serviceMode,
  editing,
  initialQuantity=1,
  selections,
  selectedVariationId,
  comboEnabled,
  comboSelections,
  comboChildren,
  toggleChild,
  setVariation,
  setComboEnabled,
  toggle,
  toggleCombo,
  onClose,
  onAdd,
}:{
  product:SmmProduct;
  family?:BentoFamilyViewV1;
  onFamilyProduct?:(productId:string)=>void;
  menu:SmmReadModelSnapshot['menu'];
  serviceMode:SmmServiceMode;
  editing:boolean;
  initialQuantity?:number;
  selections:SmmSelectionState;
  selectedVariationId:string|null;
  comboEnabled:boolean;
  comboSelections:SmmComboSelectionState;
  comboChildren:SmmComboChildSelectionState;
  toggleChild:(choice:SmmResolvedComboChoice,groupId:string,optionId:string)=>void;
  setVariation:(id:string)=>void;
  setComboEnabled:(value:boolean)=>void;
  toggle:(groupId:string,optionId:string)=>void;
  toggleCombo:(poolId:string,groupId:string,choiceId:string)=>void;
  onClose:()=>void;
  onAdd:(quantity:number)=>void;
}){
  const [drinkGroupKey,setDrinkGroupKey]=useState<string|null>(null);
  const [quantity,setQuantity]=useState(()=>Number.isSafeInteger(initialQuantity)?Math.min(99,Math.max(1,initialQuantity)):1);
  const validation=validateSmmSelections(product,selections);
  const variationOk=!product.variationRequired||Boolean(selectedVariationId);
  const requiredGroups=product.optionGroups.filter(group=>group.required||group.minSelections>0);
  const optionalGroups=product.optionGroups.filter(group=>!group.required&&group.minSelections===0);
  const orderedGroups=[...requiredGroups,...optionalGroups];
  const combo=resolveSmmProductCombo(product,menu);
  const comboValidation=validateSmmComboSelections(product,menu,comboEnabled,comboSelections,comboChildren);
  const selectedChoices=comboEnabled&&combo?combo.groups.flatMap(group=>(comboSelections[group.key]??[]).flatMap(id=>{const choice=findSmmComboChoice(group,id);return choice?[choice]:[];})):[];
  const baseMinorRaw=serviceMode==='DINE_IN'?product.publishedDineInUnitPriceMinor:product.publishedTakeawayUnitPriceMinor;
  const baseMinor=Number.isSafeInteger(Number(baseMinorRaw))?Number(baseMinorRaw):null;
  const selectedAdjustmentMinor=product.optionGroups.reduce((sum,group)=>{
    const selected=selections[group.optionGroupId]??[];
    return sum+selected.reduce((groupSum,optionId)=>{
      const option=group.options.find(candidate=>candidate.optionId===optionId);
      const adjustment=Number(option?.publishedAdjustmentMinor??0);
      return groupSum+(Number.isSafeInteger(adjustment)?adjustment:0);
    },0);
  },0);
  const selectedComboAdjustmentMinor=comboEnabled&&combo
    ?combo.groups.reduce((sum,group)=>{
      const selected=comboSelections[group.key]??[];
      return sum+selected.reduce((groupSum,choiceId)=>{
        const choice=findSmmComboChoice(group,choiceId);
        return groupSum+(choice?.publishedAdjustmentMinor??0);
      },0);
    },0)
    :0;
  const comboBaseMinor=product.mainMeal?baseMinor:combo?combo.combo.publishedBasePriceMinor+combo.mainAdjustmentMinor:baseMinor;
  const draftBaseMinor=comboEnabled&&combo?comboBaseMinor:baseMinor;
  const selectedChildAdjustmentMinor=selectedChoices.reduce((sum,choice)=>sum+choice.optionGroups.reduce((n,group)=>n+group.options.filter(option=>(comboChildren[choice.key]?.[group.optionGroupId]??[]).includes(option.optionId)).reduce((v,option)=>v+Number(option.publishedAdjustmentMinor??0),0),0),0);
  const draftUnitMinor=draftBaseMinor===null?null:draftBaseMinor+selectedAdjustmentMinor+selectedComboAdjustmentMinor+selectedChildAdjustmentMinor;
  const draftTotalMinor=draftUnitMinor===null?null:smmLineTotalMinor(draftUnitMinor,quantity);
  const deltaLabel=(minor:number)=>{
    if(minor===0)return '不加價';
    return (minor>0?'+':'')+money('HKD',minor);
  };
  const firstIssue=validation.issues[0]??comboValidation.issues[0];
  const drinkGroup=combo?.groups.find(group=>group.key===drinkGroupKey);

  if(drinkGroup)return <FocusDialog onClose={()=>setDrinkGroupKey(null)}><div className="overlay ui-drink-overlay"><section className="sheet ui-drink-sheet" role="dialog" aria-modal="true" aria-label="選擇套餐飲品">
    <header><div><h2>{drinkGroup.group.name}</h2></div><button type="button" aria-label="返回商品設定" onClick={()=>setDrinkGroupKey(null)}>✕</button></header>
    <div className="stage2-option-grid">{drinkGroup.choices.map(choice=>{
      const selected=comboSelections[drinkGroup.key]??[];
      const active=selected.some(id=>findSmmComboChoice(drinkGroup,id)?.key===choice.key);
      return <button key={choice.key} type="button" className={active?'active':''} aria-pressed={active} disabled={!choice.available||(drinkGroup.effectiveMaxSelections>1&&selected.length>=drinkGroup.effectiveMaxSelections&&!active)} onClick={()=>toggleCombo(drinkGroup.pool.poolId,drinkGroup.group.groupId,choice.key)}><span>{choice.choiceLabel}{publishedAdjustmentLabel(choice.publishedAdjustmentMinor)}</span>{!choice.available?<small>暫停供應</small>:null}</button>;
    })}</div>
    <ComboChildOptions choices={selectedChoices.filter(choice=>choice.poolId===drinkGroup.pool.poolId&&choice.groupId===drinkGroup.group.groupId)} children={comboChildren} toggle={toggleChild}/>
    <footer><div><small>本款合計</small><strong>{draftUnitMinor===null?'價格更新中':money('HKD',draftUnitMinor)}</strong></div><button className="primary" type="button" onClick={()=>setDrinkGroupKey(null)}>完成飲品選擇</button></footer>
  </section></div></FocusDialog>;

  return <FocusDialog onClose={onClose}><div className="overlay stage2-overlay">
    <section className="sheet stage2-product-sheet" role="dialog" aria-modal="true" aria-label={product.name+' 商品設定'}>
      <div className="sheet-grabber"/>
      <header className="stage2-sheet-header">
        <div>
          <span>{editing?'編輯商品':'商品客製'}</span>
          <h2>{product.name}</h2>
          <strong className="v3-product-base-price">{draftBaseMinor===null?'價格更新中':money('HKD',draftBaseMinor)}</strong>
        </div>
        <button className="stage2-close" onClick={onClose} aria-label="關閉商品客製">✕</button>
      </header>

      <div className="stage2-scroll-body">
        {family?<section className="stage2-config-section" aria-label="便當飯底">
          <div className="stage2-section-head"><div><strong>飯底</strong></div></div>
          <div className="stage2-option-grid">{family.members.map(member=>{
            const candidate=menu?.products.find(item=>item.productId===member.productId);
            const unit=serviceMode==='DINE_IN'?candidate?.publishedDineInUnitPriceMinor:candidate?.publishedTakeawayUnitPriceMinor;
            return <button type="button" key={member.productId} className={product.productId===member.productId?'active':''} aria-pressed={product.productId===member.productId} disabled={!candidate?.available} onClick={()=>onFamilyProduct?.(member.productId)}><span>{member.label}</span><small>{!candidate?.available?'暫停供應':typeof unit==='number'?money('HKD',unit):'價格待更新'}</small></button>;
          })}</div>
        </section>:null}
        <section className="stage2-product-summary" aria-label="商品摘要">
          <ProductMedia product={product} className="stage2-product-media"/>
          <div>
            <small>{comboEnabled&&combo?'套餐價':serviceMode==='DINE_IN'?'堂食':'外賣'}</small>
            <strong>{draftBaseMinor===null?'價格更新中':money('HKD',draftBaseMinor)}</strong>
            <span>加配 {deltaLabel(selectedAdjustmentMinor)}</span>
            {comboEnabled?<span>套餐內容 {deltaLabel(selectedComboAdjustmentMinor)}</span>:null}
            <b>{draftUnitMinor===null?'總價更新中':money('HKD',draftUnitMinor)}</b>
          </div>
        </section>

        {product.variations?.length?<section className={`stage2-config-section ${product.variationRequired&&!variationOk?'has-error':''}`}>
          <div className="stage2-section-head">
            <div><strong>規格</strong><small>{product.variationRequired?'必選':'可選'}</small></div>
            <span>{selectedVariationId?'已選 1':'未選'}</span>
          </div>
          <div className="stage2-option-grid">
            {product.variations.map(item=><button
              key={item.variationId}
              disabled={!item.available}
              className={selectedVariationId===item.variationId?'active':''}
              onClick={()=>setVariation(item.variationId)}
            ><span>{item.name}</span>{!item.available?<small>暫停供應</small>:null}</button>)}
          </div>
          {product.variationRequired&&!variationOk?<p className="stage2-inline-error">請先選擇必選規格。</p>:null}
        </section>:null}

        <div className="v3-product-groups">{orderedGroups.map(group=>{
          const selected=selections[group.optionGroupId]??[];
          const min=Math.max(group.required?1:0,group.minSelections);
          const groupError=selected.length<min||selected.length>group.maxSelections||selected.some(id=>!group.options.find(option=>option.optionId===id&&option.available));
          const maxReached=group.maxSelections>1&&selected.length>=group.maxSelections;
          return <section className={`stage2-config-section ${groupError?'has-error':''}`} key={group.optionGroupId}>
            <div className="stage2-section-head"><div><strong>{group.name}</strong><small>{min>0?'必揀 '+min+' 項':'可選'}{group.maxSelections>1?'・最多 '+group.maxSelections+' 項':''}</small></div></div>
            <div className="stage2-option-grid">{group.options.map(option=>{
              const active=selected.includes(option.optionId);
              const adjustment=Number(option.publishedAdjustmentMinor??0);
              return <button key={option.optionId} type="button" disabled={!option.available||(maxReached&&!active)} className={active?'active':''} aria-pressed={active} onClick={()=>toggle(group.optionGroupId,option.optionId)}><span>{frontlineOptionName(option)}{publishedAdjustmentLabel(Number.isSafeInteger(adjustment)?adjustment:0)}</span>{!option.available?<small>暫停供應</small>:null}</button>;
            })}</div>
            {groupError?<p className="stage2-inline-error">{selected.length<min?`最少需要選擇 ${min} 項。`:selected.length>group.maxSelections?`最多只可以選擇 ${group.maxSelections} 項。`:'已選項目暫停供應，請重新選擇。'}</p>:null}
          </section>;
        })}</div>

        {combo?<section className={`stage2-config-section stage2-combo-section ${product.mainMeal?'stage2-main-meal':''} ${comboEnabled&&!comboValidation.ok?'has-error':''}`}>
          <div className="stage2-section-head stage2-combo-head">
            <div>
              <strong>{product.mainMeal?'套餐飲品':'套餐'}</strong>
              <small>{combo.combo.name}</small>
            </div>
            <button
              type="button"
              className={`stage2-combo-toggle ${comboEnabled?'active':''}`}
              aria-pressed={comboEnabled}
              onClick={()=>setComboEnabled(!comboEnabled)}
            >{comboEnabled?'已選套餐':'升級套餐'}</button>
          </div>
          {comboEnabled?<>
            <div className="stage2-combo-base">
              <span>套餐基礎價</span>
              <strong>{comboBaseMinor===null?'價格更新中':money('HKD',comboBaseMinor)}</strong>
            </div>
            {combo.groups.map(group=>{
              const selected=comboSelections[group.key]??[];
              const groupError=selected.length<group.effectiveMinSelections||
                selected.length>group.effectiveMaxSelections||
                selected.some(id=>!findSmmComboChoice(group,id)?.available);
              const maxReached=group.effectiveMaxSelections>1&&selected.length>=group.effectiveMaxSelections;
              return <section className={`stage2-combo-group ${groupError?'has-error':''}`} key={group.key}>
                <div className="stage2-section-head">
                  <div>
                    <strong>{group.group.name}</strong>
                    <small>{group.effectiveMinSelections>0?'必選':'可選'} · 最少 {group.effectiveMinSelections} · 最多 {group.effectiveMaxSelections}</small>
                  </div>
                  <span>已選 {selected.length}/{group.effectiveMaxSelections}</span>
                </div>
                {group.pool.addonKind==='DRINK'?<button type="button" className="ui-drink-open" onClick={()=>setDrinkGroupKey(group.key)}><span>{selected.length?selected.flatMap(id=>{const choice=findSmmComboChoice(group,id);return choice?[choice.choiceLabel]:[];}).join('、'):'選擇飲品（可稍後決定）'}</span><b>›</b></button>:<div className="stage2-option-grid">
                  {group.choices.map(choice=>{
                    const active=selected.some(id=>findSmmComboChoice(group,id)?.key===choice.key);
                    const disabled=!choice.available||(maxReached&&!active);
                    return <button
                      key={choice.key}
                      type="button"
                      disabled={disabled}
                      className={active?'active':''}
                      aria-pressed={active}
                      onClick={()=>toggleCombo(group.pool.poolId,group.group.groupId,choice.key)}
                    >
                      <span>{choice.choiceLabel}{publishedAdjustmentLabel(choice.publishedAdjustmentMinor)}</span>
                      {!choice.available?<small>暫停供應</small>:null}
                    </button>;
                  })}
                </div>}
                {group.pool.addonKind!=='DRINK'?<ComboChildOptions choices={selectedChoices.filter(choice=>choice.poolId===group.pool.poolId&&choice.groupId===group.group.groupId)} children={comboChildren} toggle={toggleChild}/>:null}
                {groupError?<p className="stage2-inline-error">
                  {selected.length<group.effectiveMinSelections?`最少需要選擇 ${group.effectiveMinSelections} 項。`:selected.length>group.effectiveMaxSelections?`最多只可以選擇 ${group.effectiveMaxSelections} 項。`:'已選套餐內容包含暫停供應項目，請重新選擇。'}
                </p>:null}
              </section>;
            })}
          </>:null}
        </section>:null}

        {(!validation.ok||!comboValidation.ok)&&firstIssue?<section className="stage2-validation-summary" role="status">
          <strong>仲差一個選擇</strong>
          <span>{firstIssue}</span>
        </section>:null}
      </div>

      <footer className="stage2-sticky-footer">
        <div className="stage2-quantity" aria-label="商品份數">
          <button type="button" disabled={quantity<=1} onClick={()=>setQuantity(value=>Math.max(1,value-1))} aria-label="減少份數">−</button><b>{quantity}</b><button type="button" disabled={quantity>=99} onClick={()=>setQuantity(value=>Math.min(99,value+1))} aria-label="增加份數">＋</button>
        </div>
        <button className="primary" disabled={!validation.ok||!variationOk||!comboValidation.ok} onClick={()=>onAdd(quantity)}>
          <span>{!variationOk?'請選擇規格':!validation.ok||!comboValidation.ok?'請完成必選項':editing?'儲存修改':'加入購物車'}</span>
          <small>{draftTotalMinor===null?'價格更新中':money('HKD',draftTotalMinor)}</small>
        </button>
      </footer>
    </section>
  </div></FocusDialog>;
}

export function CartSheet({cart,quote,menu,checkoutStage,serviceMode,tender,diningTarget,diningTables,note,onNote,onServiceMode,onTender,onChooseDiningTarget,onClose,onBackToCart,onEdit,onAcceptRefresh,onQuantity,onRemove,onClear,onCheckout,onSubmitBoundary}:{
  cart:readonly SmmCartLine[];
  quote:SmmQuoteSnapshot|null;
  menu:SmmReadModelSnapshot['menu'];
  checkoutStage:boolean;
  serviceMode:SmmServiceMode;
  tender:SmmTender;
  diningTarget:SmmDiningTarget|null;
  diningTables:NonNullable<SmmReadModelSnapshot['diningTables']>;
  note:string;
  onNote:(value:string)=>void;
  onServiceMode:(mode:SmmServiceMode)=>void;
  onTender:(tender:SmmTender)=>void;
  onChooseDiningTarget:()=>void;
  onClose:()=>void;
  onBackToCart:()=>void;
  onEdit:(line:SmmCartLine)=>void;
  onAcceptRefresh:(lineId:string)=>void;
  onQuantity:(id:string,q:number)=>void;
  onRemove:(id:string)=>void;
  onClear:()=>void;
  onCheckout:()=>void;
  onSubmitBoundary:()=>void;
}){
  const clearDialog=useRef<HTMLDialogElement>(null);
  if(checkoutStage)return <FocusDialog onClose={onBackToCart}><Stage4CheckoutView cart={cart} quote={quote} menu={menu} serviceMode={serviceMode} tender={tender} diningTarget={diningTarget} diningTables={diningTables} note={note} onServiceMode={onServiceMode} onTender={onTender} onChooseDiningTarget={onChooseDiningTarget} onClose={onClose} onBackToCart={onBackToCart} onSubmitBoundary={onSubmitBoundary}/></FocusDialog>;
  const rows=cart.map(line=>Object.freeze({line,attention:line.refreshAttention??null}));
  const affectedCount=rows.filter(row=>Boolean(row.attention)).length;
  const validRows=rows.filter(row=>!row.attention&&Number.isSafeInteger(Number(row.line.publishedUnitPriceMinor)));
  const validItemCount=validRows.reduce((sum,row)=>sum+row.line.quantity,0);
  const validSubtotalMinor=validRows.reduce((sum,row)=>sum+Number(row.line.publishedUnitPriceMinor)*row.line.quantity,0);
  const itemCount=cart.reduce((sum,line)=>sum+line.quantity,0);
  const checkoutReady=Boolean(menu&&quote&&cart.length&&affectedCount===0);

  return <FocusDialog onClose={onClose}><div className="overlay stage3-overlay">
    <section className="sheet stage3-cart-sheet" role="dialog" aria-modal="true" aria-label="購物草稿">
      <div className="sheet-grabber"/>
      <header className="stage3-cart-header">
        <div>
          <h2>購物車 <span>{itemCount}</span></h2>
        </div>
        <div className="stage3-header-actions">
          {cart.length?<button className="stage3-clear" type="button" onClick={()=>clearDialog.current?.showModal()}>清空</button>:null}
          <button className="stage3-close" type="button" onClick={onClose} aria-label="關閉購物草稿">✕</button>
        </div>
      </header>

      <div className="stage3-scroll-body">
        <section className="stage3-mode" aria-label="服務方式">
          <div className="stage3-segmented">
            <button className={serviceMode==='DINE_IN'?'active':''} type="button" aria-pressed={serviceMode==='DINE_IN'} onClick={()=>onServiceMode('DINE_IN')}>堂食</button>
            <button className={serviceMode==='TAKEAWAY'?'active':''} type="button" aria-pressed={serviceMode==='TAKEAWAY'} onClick={()=>onServiceMode('TAKEAWAY')}>外賣</button>
          </div>
        </section>

        {!menu&&cart.length?<section className="stage3-sync-warning" role="status">
          <strong>餐單更新中</strong>
          <span>你揀好嘅商品會保留，資料更新後就可以繼續結帳。</span>
        </section>:null}

        {affectedCount>0?<section className="stage3-repair-summary" role="status">
          <strong>有 {affectedCount} 項需要調整</strong>
          <span>只修正有問題嗰一行；其他購物草稿保持不變。</span>
        </section>:null}

        {!cart.length?<section className="stage3-empty">
          <span className="stage3-empty-bag" aria-hidden="true">♡</span>
          <h3>購物車仲係空嘅</h3>
          <p>去點單揀返你想食嘅餐點。</p>
          <button className="primary" type="button" onClick={onClose}>去點單</button>
        </section>:<div className="stage3-lines">
          {rows.map(({line,attention})=>{
            const drinkPools=menu?.combos?.find(combo=>combo.comboId===line.combo?.comboId)?.addonPoolIds.filter(poolId=>menu?.comboPools?.some(pool=>pool.poolId===poolId&&pool.addonKind==='DRINK'))??[];
            const drinkPending=Boolean(line.combo&&drinkPools.some(poolId=>!line.combo!.selections.some(item=>item.poolId===poolId)));
            const summary=[line.riceballDrinkPromotion?'飯團優惠飲品':'',
              line.combo?.comboName,
              line.selectedVariationName,
              ...(drinkPending?['飲品待定']:[]),
              ...smmCartDisplayDetails(line,menu).map(detail=>detail.label+publishedAdjustmentLabel(detail.publishedAdjustmentMinor)),
            ].filter(Boolean).join(' · ');
            const unitMinor=Number.isSafeInteger(Number(line.publishedUnitPriceMinor))
              ?Number(line.publishedUnitPriceMinor)
              :null;
            const lineTotalMinor=smmLineTotalMinor(line.publishedUnitPriceMinor,line.quantity);
            const lineTotal=lineTotalMinor===null?'價格待同步':money('HKD',lineTotalMinor);
            const proposedUnit=attention&&Number.isSafeInteger(Number(attention.proposedPublishedUnitPriceMinor))
              ?Number(attention.proposedPublishedUnitPriceMinor)
              :null;
            const priceChanged=attention&&unitMinor!==null&&proposedUnit!==null&&unitMinor!==proposedUnit;
            const product=menu?.products.find(item=>item.productId===line.productId);
            const mediaProduct=product??{productId:line.productId,name:line.productName,imageRef:undefined};
            return <article className={`stage3-line ${attention?'has-attention':''}`} key={line.lineId}>
              <ProductMedia product={mediaProduct} className="stage3-line-media"/>
              <div className="stage3-line-main">
                <div className="stage3-line-title">
                  <div><strong><span className="stage3-line-count">{line.quantity}×</span> {line.productName}</strong>{summary?<small>{summary}</small>:null}</div>
                  <strong className="stage3-line-amount">{lineTotal}</strong>
                </div>
                <div className="stage3-line-bottom">
                  <button className="stage3-edit" type="button" onClick={()=>onEdit(line)} aria-label={'編輯 '+line.productName}>改選項</button>
                  <div className="stage3-qty" aria-label={line.productName+' 數量'}>
                    {line.quantity===1?<button type="button" className="stage3-remove" onClick={()=>onRemove(line.lineId)} aria-label={'移除 '+line.productName}><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7"/></svg></button>:<button type="button" onClick={()=>onQuantity(line.lineId,line.quantity-1)} aria-label="減少數量">−</button>}
                    <b>{line.quantity}</b>
                    <button type="button" disabled={line.quantity>=99} onClick={()=>onQuantity(line.lineId,line.quantity+1)} aria-label="增加數量">＋</button>
                  </div>
                </div>
                {attention?<div className="stage3-line-attention">
                  <div>
                    <b>{attention.kind==='PRICE_CHANGED'?'價格有更新':'商品內容有更新'}</b>
                    <span>{attention.kind==='PRICE_CHANGED'?'餐單價格已更新，請確認新價格。':'商品／套餐資料已更新，請確認或重新編輯。'}</span>
                    {priceChanged?<small>舊價 {money('HKD',unitMinor!)} → 新價 {money('HKD',proposedUnit!)}</small>:null}
                  </div>
                  <div className="stage3-attention-actions">
                    {attention.canAccept?<button type="button" onClick={()=>onAcceptRefresh(line.lineId)}>接受更新</button>:null}
                    <button type="button" onClick={()=>onEdit(line)}>重新編輯</button>
                  </div>
                </div>:null}
              </div>
            </article>;
          })}
        </div>}

        {cart.length?<div className="ui-note-chips" aria-label="快捷備註">{['少辣','走冰','分開袋'].map(text=><button key={text} type="button" aria-pressed={note.split('、').includes(text)} onClick={()=>onNote(note.split('、').includes(text)?note.split('、').filter(value=>value!==text).join('、'):[note,text].filter(Boolean).join('、'))}>{text}</button>)}</div>:null}
        {cart.length?<label className="stage3-note">
          <span>訂單備註 <small>選填</small></span>
          <textarea value={note} maxLength={160} onChange={event=>onNote(event.target.value)} placeholder="例如：請分開袋"/>
          <small>{note.length}/160</small>
        </label>:null}
      </div>

      <footer className="stage3-footer">
        <div>
          <span>{affectedCount?'可用小計 · '+validItemCount+' 件':'合計'}</span>
          <strong>{!cart.length?'未有商品':affectedCount?money('HKD',validSubtotalMinor):(quote?money(quote.currency,quote.totalMinor):'價格待同步')}</strong>
        </div>
        <button className="primary stage3-checkout" type="button" disabled={!checkoutReady} onClick={onCheckout}>去結帳</button>
      </footer>
      <dialog className="ui-clear-cart-dialog" ref={clearDialog} aria-label="清空購物車確認"><h2>清空 {itemCount} 件商品？</h2><div><button type="button" onClick={()=>clearDialog.current?.close()}>取消</button><button className="danger" type="button" onClick={()=>{clearDialog.current?.close();onClear();}}>確認清空</button></div></dialog>
    </section>
  </div></FocusDialog>;
}

function Stage4CheckoutView({cart,quote,menu,serviceMode,tender,diningTarget,diningTables,note,onServiceMode,onTender,onChooseDiningTarget,onClose,onBackToCart,onSubmitBoundary}:{
  cart:readonly SmmCartLine[];
  quote:SmmQuoteSnapshot|null;
  menu:SmmReadModelSnapshot['menu'];
  serviceMode:SmmServiceMode;
  tender:SmmTender;
  diningTarget:SmmDiningTarget|null;
  diningTables:NonNullable<SmmReadModelSnapshot['diningTables']>;
  note:string;
  onServiceMode:(mode:SmmServiceMode)=>void;
  onTender:(tender:SmmTender)=>void;
  onChooseDiningTarget:()=>void;
  onClose:()=>void;
  onBackToCart:()=>void;
  onSubmitBoundary:()=>void;
}){
  const [cashReceived,setCashReceived]=useState('');
  const itemCount=cart.reduce((sum,line)=>sum+line.quantity,0);
  const hasAttention=cart.some(line=>Boolean(line.refreshAttention));
  const diningStatus=smmStage4DiningTargetStatus(serviceMode,diningTarget,diningTables);
  const submitReady=smmStage4CheckoutReady({
    cartLength:cart.length,
    totalMinor:quote?.totalMinor,
    hasAttention,
    tender,
    serviceMode,
    diningTargetValid:diningStatus.valid,
  });
  const serviceLabel=serviceMode==='DINE_IN'?'堂食':'外賣';

  return <div className="overlay stage4-overlay">
    <section className="sheet stage4-checkout-sheet" role="dialog" aria-modal="true" aria-label="結帳">
      <div className="sheet-grabber"/>
      <header className="stage4-header">
        <button type="button" className="stage4-back" onClick={onBackToCart} aria-label="返回購物車">‹</button><h2>結帳</h2>
        <button className="stage4-close" type="button" onClick={onClose} aria-label="關閉結帳">✕</button>
      </header>

      <div className="stage4-scroll-body">
        <section className="stage4-section">
          <div className="stage4-section-head">
            <div><strong>服務方式</strong></div>
            <span>1</span>
          </div>
          <div className="stage4-segmented">
            <button type="button" className={serviceMode==='TAKEAWAY'?'active':''} onClick={()=>onServiceMode('TAKEAWAY')}>外賣</button>
            <button type="button" className={serviceMode==='DINE_IN'?'active':''} onClick={()=>onServiceMode('DINE_IN')}>堂食</button>
          </div>
        </section>

        {serviceMode==='DINE_IN'?<section className={`stage4-section stage4-dining ${diningStatus.valid?'':'required'}`}>
          <div className="stage4-section-head">
            <div><strong>堂食去向</strong><small>揀枱號；未有座位就先加入輪候。</small></div>
            <span>2</span>
          </div>
          <div className="stage4-target-row">
            <div>
              <small>{diningStatus.valid?'已選擇':'必選'}</small>
              <strong>{diningStatus.label}</strong>
            </div>
            <button type="button" className={diningStatus.valid?'':'primary'} onClick={onChooseDiningTarget}>{diningStatus.valid?'更改':'選擇餐枱／輪候'}</button>
          </div>
          {!diningStatus.valid?<p className="stage4-inline-warning">堂食需要先揀枱號或者加入輪候。</p>:null}
        </section>:null}

        {serviceMode==='TAKEAWAY'?<section className="stage4-section">
          <div className="stage4-section-head">
            <div><strong>付款方式</strong></div>
            <span>2</span>
          </div>
          <div className="stage4-tender-grid">
            {SMM_STAGE4_TENDERS.map(row=><button key={row.value} type="button" className={tender===row.value?'active':''} aria-pressed={tender===row.value} onClick={()=>onTender(row.value)}>{row.label}</button>)}
          </div>
          {tender==='CASH'?<div className="ui-cash-reference"><label>客人付現金<input aria-label="客人付現金" inputMode="decimal" placeholder="輸入金額" value={cashReceived} onChange={event=>{const value=event.target.value;if(/^\d{0,7}(\.\d{0,2})?$/.test(value))setCashReceived(value)}}/></label><strong>{cashReceived&&quote?(Math.round(Number(cashReceived)*100)>=quote.totalMinor?'找續 '+money(quote.currency,Math.round(Number(cashReceived)*100)-quote.totalMinor):'尚欠 '+money(quote.currency,quote.totalMinor-Math.round(Number(cashReceived)*100))):'找續參考'}</strong><small>只供找續計算；實際收款由收銀機確認。</small></div>:null}
        </section>:<section className="stage4-section"><strong>堂食先落單，食完先埋單</strong><small>今次只建立／追加堂食單，唔會記錄付款方式。</small></section>}

        <section className="stage4-section stage4-summary">
          <div className="stage4-section-head">
            <div><strong>今張單</strong></div>
            <span>{serviceMode==='DINE_IN'?'4':'3'}</span>
          </div>

          <div className="stage4-review-lines">
            {cart.map(line=>{
              const total=smmLineTotalMinor(line.publishedUnitPriceMinor,line.quantity);
              const product=menu?.products.find(item=>item.productId===line.productId);
              const mediaProduct=product??{productId:line.productId,name:line.productName,imageRef:undefined};
              return <div key={line.lineId}>
                <ProductMedia product={mediaProduct} className="stage4-line-media"/>
                <span>{line.productName} × {line.quantity}
                  {line.combo?<small>{line.combo.comboName}</small>:null}
                  {smmCartDisplayDetails(line,menu).map((detail,index)=><small key={index}>{detail.label}{publishedAdjustmentLabel(detail.publishedAdjustmentMinor)}</small>)}
                </span>
                <strong>{total===null?'價格更新中':money('HKD',total)}</strong>
              </div>;
            })}
          </div>

          {note.trim()?<div className="stage4-note-summary"><span>備註</span><p>{note}</p></div>:null}

          <div className="stage4-total">
            <div><span>總額</span><small>{quote?'已按最新餐單資料計算':'價格更新中'}</small></div>
            <strong>{quote?money(quote.currency,quote.totalMinor):'—'}</strong>
          </div>

          {hasAttention?<section className="stage4-blocker" role="alert">
            <strong>有商品資料更新咗</strong>
            <span>返回購物車確認有變更嘅商品，就可以繼續落單。</span>
          </section>:null}

        </section>
      </div>

      <footer className="stage4-footer" data-menu-revision={menu?.revision??''}>
        <div className="ui-checkout-total"><span>合計・{itemCount} 件</span><strong>{quote?money(quote.currency,quote.totalMinor):'—'}</strong></div>
        <button className="primary stage4-submit" type="button" disabled={!submitReady} onClick={onSubmitBoundary}>確認落單</button>
        {!submitReady?<small>{serviceMode==='DINE_IN'&&!diningStatus.valid?'請先揀枱號或加入輪候。':hasAttention?'請先確認商品更新。':'價格更新中。'}</small>:null}
      </footer>
    </section>
  </div>;
}


function DiningTargetSheet({tables,covers,setCovers,onClose,onSelect}:{
  tables:NonNullable<SmmReadModelSnapshot['diningTables']>;
  covers:number;
  setCovers:(value:number)=>void;
  onClose:()=>void;
  onSelect:(target:SmmDiningTarget)=>void;
}){
  return <FocusDialog onClose={onClose}><div className="overlay"><section className="sheet dining-target-sheet" role="dialog" aria-modal="true" aria-label="掛枱／輪候"><div className="sheet-grabber"/><header><div><span>堂食去向</span><h2>掛枱／輪候</h2><small>揀枱後會掛入該枱；如果已有堂食單就直接加單。</small></div><button onClick={onClose} aria-label="關閉掛枱／輪候">✕</button></header>
    <section className="option-group"><div><strong>人數</strong><span>1–30 位</span></div><div className="qty"><button aria-label="減少人數" onClick={()=>setCovers(Math.max(1,covers-1))}>−</button><b>{covers}</b><button aria-label="增加人數" onClick={()=>setCovers(Math.min(30,covers+1))}>＋</button></div></section>
    <section className="option-group"><div><strong>掛入邊張枱</strong><span>已有單＝加單；空枱＝開枱</span></div>{tables.length?<div className="option-list">{tables.map(row=><button key={row.tableId} onClick={()=>onSelect({kind:'TABLE',tableId:row.tableId,covers})}>{row.label}</button>)}</div>:<p className="callout">目前未有可用枱號。</p>}</section>
    <section className="option-group"><div><strong>未決定座位</strong><span>先放入輪候</span></div><button className="primary" onClick={()=>onSelect({kind:'WAITING',covers})}>加入輪候</button></section>
  </section></div></FocusDialog>;
}

function ConnectionSettings({snapshot}:{snapshot:SmmReadModelSnapshot|null}){
  const path=snapshot?.connectionPath;
  return <div className="connection-settings">
    <p className="callout">目前使用：<strong>{path==='LAN'?'LAN 直接門店':path==='INTERNET'?'Internet':'未連接'}</strong>。LAN 只係可選加速／直接門店通道；唔支援 LAN 嘅瀏覽器仍然會用 Internet 正常同步餐單、規則同更新。</p>
    <LanSetup onReady={()=>location.reload()}/>
  </div>;
}

function LanSetup({onReady}:{onReady:()=>void}){
  const saved=readSmmLanPwaConfig();
  const [host,setHost]=useState(saved?.host??'');
  const [port,setPort]=useState(saved?.port??17831);
  const [deviceId,setDeviceId]=useState(saved?.deviceId??('SMM-'+crypto.randomUUID().slice(0,8)));
  const [token,setToken]=useState(saved?.pairingToken??'');
  const [state,setState]=useState('可選：LAN 只係直接門店通道；Safari／Chrome 如唔支援，Internet 仍會同步餐單、規則同更新。');
  const config={host:host.trim(),port,deviceId:deviceId.trim(),pairingToken:token.trim()};
  return <section className="panel lan-setup"><h2>店內直接連線（可選）</h2><p>{state}</p>
    <label>收銀機位址<input value={host} onChange={e=>setHost(e.target.value)} placeholder="例如 192.168.1.20"/></label>
    <label>連接埠<input type="number" value={port} onChange={e=>setPort(Number(e.target.value)||17831)}/></label>
    <label>裝置名稱<input value={deviceId} onChange={e=>setDeviceId(e.target.value)}/></label>
    <label>配對碼<input value={token} onChange={e=>setToken(e.target.value)} placeholder="由收銀機復原頁顯示"/></label>
    <div className="lan-actions">
      <button onClick={async()=>{try{setState('測試中…');await probeSmmLan(config);setState('瀏覽器可以連到收銀機。');}catch{setState('呢個瀏覽器目前未能直接連收銀機；可以繼續使用其他落單方式。');}}}>測試 LAN</button>
      <button className="primary" onClick={async()=>{try{setState('配對中…');await pairSmmLan(config);saveSmmLanPwaConfig(config);setState('配對成功。');onReady();}catch{setState('配對未成功；唔會阻止其他落單方式。');}}}>配對並使用</button>
    </div>
  </section>;
}

function EmptyState({title,detail,children}:{title:string;detail:string;children?:React.ReactNode}){
  return <section className="panel empty-state"><h2>{title}</h2><p>{detail}</p>{children}</section>;
}
function Metric({label,value}:{label:string;value:string}){return <div><small>{label}</small><strong>{value}</strong></div>}
function NavGlyph({label}:{label:string}){
  const common={viewBox:'0 0 24 24',width:22,height:22,fill:'none',stroke:'currentColor',strokeWidth:1.8,strokeLinecap:'round' as const,strokeLinejoin:'round' as const,'aria-hidden':true};
  if(label==='點單')return <svg {...common}><rect x="4" y="4" width="16" height="16" rx="4"/><path d="M8 9h8M8 13h5M8 17h3"/></svg>;
  if(label==='待處理')return <svg {...common}><path d="M7 5h10l2 3v11H5V8l2-3Z"/><path d="M9 5V3h6v2M8 11h8M8 15h5"/></svg>;
  if(label==='訂單')return <svg {...common}><path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21V3Z"/><path d="M9 8h6M9 12h6M9 16h4"/></svg>;
  if(label==='桌檯')return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></svg>;
  return <svg {...common}><circle cx="6" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="18" cy="12" r="1.5" fill="currentColor" stroke="none"/></svg>;
}
function NavButton({active,label,badge,onClick}:{active:boolean;label:string;badge?:string;onClick:()=>void}){return <button className={active?'active':''} onClick={onClick}><span className="nav-glyph"><NavGlyph label={label}/></span><small>{label}</small>{badge?<b>{badge}</b>:null}</button>}

function channelStateLabel(state:string){return state==='CONNECTED'?'正常':state==='STALE'?'資料稍舊':state==='DEGRADED'?'部分異常':state==='OFFLINE'?'離線':'狀態未明'}
function businessDayStateLabel(state:string|undefined){return state==='OPEN'?'營業中':state==='CLOSED'?'已收舖':state==='STALE'?'資料稍舊':state==='UNKNOWN'?'狀態未明':state||'未有資料'}
function capacityLabel(state:string|undefined){return state==='NORMAL'?'正常':state==='BUSY'?'繁忙':state==='PAUSED'?'已暫停':state==='UNKNOWN'?'狀態未明':'未有資料'}
function reportingFreshnessLabel(state:string|undefined){return state==='CURRENT'?'已更新':state==='STALE'?'資料稍舊':state==='UNKNOWN'?'狀態未明':'未有資料'}
function refundStateLabel(state:string){return state==='PENDING'?'待處理':state==='REVIEWING'?'跟進中':state==='RESOLVED'?'已完成':'狀態未明'}
function printStateLabel(state:string){return state==='READY'?'連線可用':state==='OFFLINE'?'未能連線':'未測試'}

function connectionLabelShort(state:SmmConnectionState){return state==='READY'?'已連接':state==='LOADING'?'同步中':state==='ERROR'?'錯誤':state==='STALE'?'資料稍舊':state==='PARTIAL'?'部分資料':state==='UNKNOWN'?'未知':'未連接'}
